import React, { useEffect, useMemo, useRef, useState } from "react";
import AvatarImg from "../components/AvatarImg";
import AppFeatureIcon from "../components/AppFeatureIcon";
import NotificationHistoryAdmin from "../components/NotificationHistoryAdmin";
import { useLanguage } from "../i18n/LanguageContext";
import { permissionTemplates } from "../utils/permissions";
import { bildirimEkleService } from "../services/bildirimService";
import EmailSystemAdmin from "../components/EmailSystemAdmin";
import { savePremiumConfig } from "../services/premiumService";
import { DEFAULT_PREMIUM_CONFIG } from "../hooks/usePremium";
import {
  tumUygulamaKullanicilariniGetir,
  seciliKullanicilaraBildirimGonder,
  tumKullanicilaraSistemDuyurusuGonder,
  adminBildirimSablonlariniGetir,
  adminBildirimSablonlariniKaydet,
} from "../services/adminNotificationService";
import { zamanliBildirimOlustur } from "../services/scheduledNotificationService";
import {
  bekleyenZamanlanmisBildirimleriGetir,
  zamanlanmisBildirimiGuncelle,
  zamanlanmisBildirimiIptalEt,
  zamanlanmisBildirimiSil,
} from "../services/scheduledNotificationAdminService";
import { bildirimAlicilariniGetir, bildirimGecmisiniGetir, sonSaatlerdekiPushlariGetir } from "../services/notificationHistoryService";
import { appAlert, appConfirm } from "../components/AppDialog";
import {
  DEFAULT_AUTOMATION_RULES,
  otomatikBildirimKurallariniGetir,
  otomatikBildirimDurumunuGetir,
  otomatikBildirimGonderimleriniGetir,
  otomatikBildirimKuraliniKaydet,
  otomatikBildirimleriSimdiCalistir,
  tekrarEdenSistemDuyurulariniGetir,
  tekrarEdenSistemDuyurusunuIptalEt,
  tekrarEdenSistemDuyurusunuGuncelle,
} from "../services/automationRulesService";
import {
  DEFAULT_NOTIFICATION_POLICIES,
  DEFAULT_NOTIFICATION_POLICY_GLOBAL,
  bildirimOtomasyonOzetiniGetir,
  bildirimPolitikalariGetir,
  bildirimPolitikalariVarsayilanaKur,
  bildirimPolitikasiKaydet,
} from "../services/notificationPolicyService";
import {
  aileBildirimiGonder,
  aileBildirimiOkunmaDurumuGetir,
  aileZamanliBildirimiGuncelle,
  aileZamanliBildirimiIptalEt,
  aileZamanliBildirimiOlustur,
  aileZamanliBildirimleriniGetir,
  sonAileBildirimleriniGetir,
} from "../services/familyNotificationService";
import AssistantNotificationAdmin from "../components/AssistantNotificationAdmin";
import EngagementAdminPanel from "../components/EngagementAdminPanel";
import AppReviewAdminPanel from "../components/AppReviewAdminPanel";
import LiveUserActivityAdmin from "../components/LiveUserActivityAdmin";
import AdminOperationsOverview from "../components/AdminOperationsOverview";
import PerformanceMonitoringAdmin from "../components/PerformanceMonitoringAdmin";
import CorporateTrafficAdmin from "../components/CorporateTrafficAdmin";
import AccountDeletionSurveyAdminPanel from "../components/AccountDeletionSurveyAdminPanel";
import imgAilem from "../assets/ailemasset.png";
import imgShopping from "../assets/alisverisasset.png";
import imgCalendar from "../assets/takvimasset.png";
import imgFinance from "../assets/finansasset.png";
import imgTasks from "../assets/gorevlerasset.png";
import imgExpenses from "../assets/harcamalarasset.png";
import imgHealth from "../assets/saglikasset.png";
import imgMap from "../assets/aileharitasiasset.png";
import imgChildren from "../assets/cocuktakibiasset.png";
import imgChat from "../assets/ailesohbetiasset.png";
import imgDocuments from "../assets/belgelerasset.png";
import imgCooking from "../assets/nepisirsemasset.png";
import imgAssistant from "../assets/aileasistaniasset.png";
import imgManagement from "../assets/yonetimasset.png";
import {
  adminUserIsPremium,
  adminUserPlatformLabel,
  adminUserPushReady,
  adminUserVersionOptions,
  filterAndSortAdminUsers,
  formatAdminExactActivity,
  formatAdminLastActive,
} from "../utils/adminNotificationUsers";

function SuperAdminAccordion({ icon, title, description, open, onToggle, children, adminLabel = "Admin" }) {
  return (
    <section className={`darkCard superAdminAccordion ${open ? "isOpen" : ""}`}>
      <button
        type="button"
        className="superAdminAccordionHeader"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="superAdminAccordionIcon" aria-hidden="true">{icon}</span>
        <span className="superAdminAccordionHeading">
          <strong>{title}</strong>
          <small>{description}</small>
        </span>
        <span className="superAdminAccordionMeta">
          <span className="superAdminAccordionBadge">{adminLabel}</span>
          <span className="superAdminAccordionChevron" aria-hidden="true">⌄</span>
        </span>
      </button>
      {open && <div className="superAdminAccordionContent">{children}</div>}
    </section>
  );
}

/* ── Tüm sayfa yetkileri ── */
const SAYFA_YETKILERI_KEYS = [
  { key: "home",           asset: imgAilem },
  { key: "shopping",       asset: imgShopping },
  { key: "calendar",       asset: imgCalendar },
  { key: "finance",        asset: imgFinance },
  { key: "tasks",          asset: imgTasks },
  { key: "sharedExpenses", asset: imgExpenses },
  { key: "health",         asset: imgHealth },
  { key: "harita",         asset: imgMap },
  { key: "children",       asset: imgChildren },
  { key: "chat",           asset: imgChat },
  { key: "documents",      asset: imgDocuments },
  { key: "nePisirsam",     asset: imgCooking },
  { key: "activity",       asset: imgAilem },
  { key: "assistant",      asset: imgAssistant },
  { key: "userManagement", asset: imgManagement },
];


const SELF_ADMIN_LOCKED_PAGE_KEYS = ["home", "userManagement"];
const SELF_ADMIN_LOCKED_ACTION_KEYS = [
  "addCategory",
  "manageUsers",
  "sendFamilyNotification",
  "manageScheduledNotifications",
  "manageNotificationAutomation",
  "sendSystemAnnouncement",
];

const familyAdminRole = (value) => [
  "aile yöneticisi",
  "aile yoneticisi",
  "family admin",
  "admin",
  "owner",
  "yönetici",
  "yonetici",
].includes(String(value || "").trim().toLowerCase());

const normalizeSelfAdminPermissions = (permissions) => ({
  pages: {
    ...(permissions?.pages || {}),
    home: true,
    userManagement: true,
  },
  actions: {
    ...(permissions?.actions || {}),
    ...Object.fromEntries(SELF_ADMIN_LOCKED_ACTION_KEYS.map((key) => [key, true])),
  },
});

/* ── Tüm işlem grupları ── */
const ISLEM_GRUPLARI_KEYS = [
  { baslik: "nav.shopping", asset: imgShopping, items: [
    { key: "addShopping",        labelKey: "perm.addShopping" },
    { key: "deleteOwnShopping",  labelKey: "perm.deleteOwnShopping" },
    { key: "deleteAllShopping",  labelKey: "perm.deleteAllShopping" },
  ]},
  { baslik: "nav.calendar", asset: imgCalendar, items: [
    { key: "addCalendarEvent",         labelKey: "perm.addCalendarEvent" },
    { key: "updateOwnCalendarEvent",   labelKey: "perm.updateOwnCalendarEvent" },
    { key: "updateAllCalendarEvents",  labelKey: "perm.updateAllCalendarEvents" },
    { key: "deleteOwnCalendarEvent",   labelKey: "perm.deleteOwnCalendarEvent" },
    { key: "deleteAllCalendarEvents",  labelKey: "perm.deleteAllCalendarEvents" },
  ]},
  { baslik: "nav.finance", asset: imgFinance, items: [
    { key: "addFinance",        labelKey: "perm.addFinance" },
    { key: "deleteOwnFinance",  labelKey: "perm.deleteOwnFinance" },
    { key: "deleteAllFinance",  labelKey: "perm.deleteAllFinance" },
  ]},
  { baslik: "nav.tasks", asset: imgTasks, items: [
    { key: "addTask",           labelKey: "perm.addTask" },
    { key: "completeOwnTask",   labelKey: "perm.completeOwnTask" },
    { key: "completeAllTasks",  labelKey: "perm.completeAllTasks" },
    { key: "deleteOwnTask",     labelKey: "perm.deleteOwnTask" },
    { key: "deleteAllTasks",    labelKey: "perm.deleteAllTasks" },
  ]},
  { baslik: "nav.sharedExpenses", asset: imgExpenses, items: [
    { key: "addSharedExpense",         labelKey: "perm.addSharedExpense" },
    { key: "deleteOwnSharedExpense",   labelKey: "perm.deleteOwnSharedExpense" },
    { key: "deleteAllSharedExpense",   labelKey: "perm.deleteAllSharedExpense" },
  ]},
  { baslik: "nav.health", asset: imgHealth, items: [
    { key: "addHealthRecord",        labelKey: "perm.addHealthRecord" },
    { key: "deleteOwnHealthRecord",  labelKey: "perm.deleteOwnHealthRecord" },
    { key: "deleteAllHealthRecords", labelKey: "perm.deleteAllHealthRecords" },
  ]},
  { baslik: "nav.map", asset: imgMap, items: [
    { key: "addMapPlace",        labelKey: "perm.addMapPlace" },
    { key: "deleteOwnMapPlace",  labelKey: "perm.deleteOwnMapPlace" },
    { key: "deleteAllMapPlaces", labelKey: "perm.deleteAllMapPlaces" },
    { key: "shareLocation",      labelKey: "perm.shareLocation" },
  ]},
  { baslik: "nav.children", asset: imgChildren, items: [
    { key: "addChildRecord",        labelKey: "perm.addChildRecord" },
    { key: "deleteOwnChildRecord",  labelKey: "perm.deleteOwnChildRecord" },
    { key: "deleteAllChildRecords", labelKey: "perm.deleteAllChildRecords" },
  ]},
  { baslik: "nav.chat", asset: imgChat, items: [
    { key: "sendMessage",       labelKey: "perm.sendMessage" },
    { key: "editOwnMessage",    labelKey: "perm.editOwnMessage" },
    { key: "deleteOwnMessage",  labelKey: "perm.deleteOwnMessage" },
    { key: "deleteAllMessages", labelKey: "perm.deleteAllMessages" },
    { key: "pinMessage",        labelKey: "perm.pinMessage" },
    { key: "sendMedia",         labelKey: "perm.sendMedia" },
    { key: "reactMessage",      labelKey: "perm.reactMessage" },
  ]},
  { baslik: "nav.documents", asset: imgDocuments, items: [
    { key: "addDocument",        labelKey: "perm.addDocument" },
    { key: "deleteOwnDocument",  labelKey: "perm.deleteOwnDocument" },
    { key: "deleteAllDocuments", labelKey: "perm.deleteAllDocuments" },
  ]},
  { baslik: "nav.cooking", asset: imgCooking, items: [
    { key: "generateMealSuggestion", labelKey: "perm.generateMealSuggestion" },
    { key: "rateMealSuggestion",     labelKey: "perm.rateMealSuggestion" },
    { key: "manageMealSuggestions",  labelKey: "perm.manageMealSuggestions" },
  ]},
  { baslik: "nav.management", asset: imgManagement, items: [
    { key: "addCategory",                  labelKey: "perm.addCategory" },
    { key: "manageUsers",                  labelKey: "perm.manageUsers" },
    { key: "sendFamilyNotification",       labelKey: "perm.sendFamilyNotification" },
    { key: "manageScheduledNotifications", labelKey: "perm.manageScheduledNotifications" },
    { key: "manageNotificationAutomation", labelKey: "perm.manageNotificationAutomation" },
    { key: "sendSystemAnnouncement",       labelKey: "perm.sendSystemAnnouncement" },
  ]},
];


const getHazirAileBildirimMesajlari = (t) => [
  {
    id: "eve-donus-saati",
    icon: "🏠",
    baslik: t("mgmt.notif.template.home.title"),
    aciklama: t("mgmt.notif.template.home.desc"),
    gonderimTuru: "normal",
  },
  {
    id: "sofra-hazir",
    icon: "🍽️",
    baslik: t("mgmt.notif.template.dinner.title"),
    aciklama: t("mgmt.notif.template.dinner.desc"),
    gonderimTuru: "normal",
  },
  {
    id: "aile-toplanma",
    icon: "👨‍👩‍👧‍👦",
    baslik: t("mgmt.notif.template.meeting.title"),
    aciklama: t("mgmt.notif.template.meeting.desc"),
    gonderimTuru: "duyuru",
  },
  {
    id: "ev-eksikleri",
    icon: "🛒",
    baslik: t("mgmt.notif.template.shopping.title"),
    aciklama: t("mgmt.notif.template.shopping.desc"),
    gonderimTuru: "normal",
  },
  {
    id: "guvendeyim",
    icon: "📍",
    baslik: t("mgmt.notif.template.location.title"),
    aciklama: t("mgmt.notif.template.location.desc"),
    gonderimTuru: "oncelikli",
  },
  {
    id: "acil-aile",
    icon: "🚨",
    baslik: t("mgmt.notify.emergencyTitle"),
    aciklama: t("mgmt.notif.template.emergency.desc"),
    gonderimTuru: "oncelikli",
  },
];

const getAileGonderimTurleri = (t) => [
  { value: "normal", label: t("mgmt.notif.priority.normal") },
  { value: "oncelikli", label: t("mgmt.notif.priority.priority") },
  { value: "duyuru", label: t("mgmt.notif.priority.announce") },
  { value: "sessiz", label: t("mgmt.notif.priority.silent") },
];

const getAileTekrarTipleri = (t) => [
  { value: "none", label: t("mgmt.repeat.once") },
  { value: "daily", label: t("mgmt.repeat.daily") },
  { value: "every3days", label: t("mgmt.repeat.every3days") },
  { value: "weekly", label: t("mgmt.repeat.weekly2") },
];

const getHazirBildirimMesajlari = (t) => [
  {
    id: "uygulamaya-don",
    icon: "👋",
    baslik: t("mgmt.auto.template.welcome.title"),
    aciklama: t("mgmt.auto.template.welcome.desc"),
  },
  {
    id: "gorevleri-kontrol",
    icon: "✅",
    baslik: t("mgmt.auto.template.tasks.title"),
    aciklama: t("mgmt.auto.template.tasks.desc"),
  },
  {
    id: "takvim-kontrol",
    icon: "📅",
    baslik: t("mgmt.notify.calendarTitle"),
    aciklama: t("mgmt.auto.template.calendar.desc"),
  },
  {
    id: "alisveris-kontrol",
    icon: "🛒",
    baslik: t("mgmt.auto.template.shopping.title"),
    aciklama: t("mgmt.auto.template.shopping.desc"),
  },
  {
    id: "belgeler-kontrol",
    icon: "📁",
    baslik: t("mgmt.notify.documentsTitle"),
    aciklama: t("mgmt.auto.template.documents.desc"),
  },
  {
    id: "genel-duyuru",
    icon: "📢",
    baslik: t("mgmt.notify.generalTitle"),
    aciklama: t("mgmt.auto.template.general.desc"),
  },
];


const getHazirSistemDuyuruKartlari = (t) => [
  { id: "bakim", icon: "🛠️", baslik: t("mgmt.sys.maintenance.title"), aciklama: t("mgmt.sys.maintenance.desc"), baslikKey: "mgmt.sys.maintenance.title", aciklamaKey: "mgmt.sys.maintenance.desc" },
  { id: "yeni-ozellik", icon: "✨", baslik: t("mgmt.sys.newfeature.title"), aciklama: t("mgmt.sys.newfeature.desc"), baslikKey: "mgmt.sys.newfeature.title", aciklamaKey: "mgmt.sys.newfeature.desc" },
  { id: "guncelleme", icon: "🚀", baslik: t("mgmt.sys.update.title"), aciklama: t("mgmt.sys.update.desc"), baslikKey: "mgmt.sys.update.title", aciklamaKey: "mgmt.sys.update.desc" },
  { id: "guvenlik", icon: "🔐", baslik: t("mgmt.sys.security.title"), aciklama: t("mgmt.sys.security.desc"), baslikKey: "mgmt.sys.security.title", aciklamaKey: "mgmt.sys.security.desc" },
  { id: "performans", icon: "⚡", baslik: t("mgmt.sys.performance.title"), aciklama: t("mgmt.sys.performance.desc"), baslikKey: "mgmt.sys.performance.title", aciklamaKey: "mgmt.sys.performance.desc" },
  { id: "bildirim", icon: "🔔", baslik: t("mgmt.sys.notification.title"), aciklama: t("mgmt.sys.notification.desc"), baslikKey: "mgmt.sys.notification.title", aciklamaKey: "mgmt.sys.notification.desc" },
  { id: "kesinti", icon: "📡", baslik: t("mgmt.sys.outage.title"), aciklama: t("mgmt.sys.outage.desc"), baslikKey: "mgmt.sys.outage.title", aciklamaKey: "mgmt.sys.outage.desc" },
  { id: "veri", icon: "🗂️", baslik: t("mgmt.sys.sync.title"), aciklama: t("mgmt.sys.sync.desc"), baslikKey: "mgmt.sys.sync.title", aciklamaKey: "mgmt.sys.sync.desc" },
  { id: "hatirlatma", icon: "📢", baslik: t("mgmt.sys.announcement.title"), aciklama: t("mgmt.sys.announcement.desc"), baslikKey: "mgmt.sys.announcement.title", aciklamaKey: "mgmt.sys.announcement.desc" },
  { id: "tesekkur", icon: "💙", baslik: t("mgmt.sys.thanks.title"), aciklama: t("mgmt.sys.thanks.desc"), baslikKey: "mgmt.sys.thanks.title", aciklamaKey: "mgmt.sys.thanks.desc" },
  { id: "newyear", icon: "🎉", baslik: t("mgmt.sys.newyear.title"), aciklama: t("mgmt.sys.newyear.desc"), baslikKey: "mgmt.sys.newyear.title", aciklamaKey: "mgmt.sys.newyear.desc" },
  { id: "ramadan", icon: "🌙", baslik: t("mgmt.sys.ramadan.title"), aciklama: t("mgmt.sys.ramadan.desc"), baslikKey: "mgmt.sys.ramadan.title", aciklamaKey: "mgmt.sys.ramadan.desc" },
  { id: "eid", icon: "🌟", baslik: t("mgmt.sys.eid.title"), aciklama: t("mgmt.sys.eid.desc"), baslikKey: "mgmt.sys.eid.title", aciklamaKey: "mgmt.sys.eid.desc" },
  { id: "birthday_app", icon: "🎂", baslik: t("mgmt.sys.birthday_app.title"), aciklama: t("mgmt.sys.birthday_app.desc"), baslikKey: "mgmt.sys.birthday_app.title", aciklamaKey: "mgmt.sys.birthday_app.desc" },
  { id: "premium_offer", icon: "💎", baslik: t("mgmt.sys.premium_offer.title"), aciklama: t("mgmt.sys.premium_offer.desc"), baslikKey: "mgmt.sys.premium_offer.title", aciklamaKey: "mgmt.sys.premium_offer.desc" },
  { id: "premium_renewal", icon: "🔁", baslik: t("mgmt.sys.premium_renewal.title"), aciklama: t("mgmt.sys.premium_renewal.desc"), baslikKey: "mgmt.sys.premium_renewal.title", aciklamaKey: "mgmt.sys.premium_renewal.desc" },
  { id: "backup_done", icon: "💾", baslik: t("mgmt.sys.backup_done.title"), aciklama: t("mgmt.sys.backup_done.desc"), baslikKey: "mgmt.sys.backup_done.title", aciklamaKey: "mgmt.sys.backup_done.desc" },
  { id: "backup_reminder", icon: "🗄️", baslik: t("mgmt.sys.backup_reminder.title"), aciklama: t("mgmt.sys.backup_reminder.desc"), baslikKey: "mgmt.sys.backup_reminder.title", aciklamaKey: "mgmt.sys.backup_reminder.desc" },
  { id: "tip_shopping", icon: "🛒", baslik: t("mgmt.sys.tip_shopping.title"), aciklama: t("mgmt.sys.tip_shopping.desc"), baslikKey: "mgmt.sys.tip_shopping.title", aciklamaKey: "mgmt.sys.tip_shopping.desc" },
  { id: "tip_calendar", icon: "📅", baslik: t("mgmt.sys.tip_calendar.title"), aciklama: t("mgmt.sys.tip_calendar.desc"), baslikKey: "mgmt.sys.tip_calendar.title", aciklamaKey: "mgmt.sys.tip_calendar.desc" },
  { id: "tip_map", icon: "🗺️", baslik: t("mgmt.sys.tip_map.title"), aciklama: t("mgmt.sys.tip_map.desc"), baslikKey: "mgmt.sys.tip_map.title", aciklamaKey: "mgmt.sys.tip_map.desc" },
  { id: "tip_finance", icon: "💰", baslik: t("mgmt.sys.tip_finance.title"), aciklama: t("mgmt.sys.tip_finance.desc"), baslikKey: "mgmt.sys.tip_finance.title", aciklamaKey: "mgmt.sys.tip_finance.desc" },
  { id: "tip_health", icon: "❤️", baslik: t("mgmt.sys.tip_health.title"), aciklama: t("mgmt.sys.tip_health.desc"), baslikKey: "mgmt.sys.tip_health.title", aciklamaKey: "mgmt.sys.tip_health.desc" },
  { id: "tip_chat", icon: "💬", baslik: t("mgmt.sys.tip_chat.title"), aciklama: t("mgmt.sys.tip_chat.desc"), baslikKey: "mgmt.sys.tip_chat.title", aciklamaKey: "mgmt.sys.tip_chat.desc" },
  { id: "feedback_request", icon: "📝", baslik: t("mgmt.sys.feedback_request.title"), aciklama: t("mgmt.sys.feedback_request.desc"), baslikKey: "mgmt.sys.feedback_request.title", aciklamaKey: "mgmt.sys.feedback_request.desc" },
  { id: "survey", icon: "📊", baslik: t("mgmt.sys.survey.title"), aciklama: t("mgmt.sys.survey.desc"), baslikKey: "mgmt.sys.survey.title", aciklamaKey: "mgmt.sys.survey.desc" },
  { id: "welcome", icon: "👋", baslik: t("mgmt.sys.welcome.title"), aciklama: t("mgmt.sys.welcome.desc"), baslikKey: "mgmt.sys.welcome.title", aciklamaKey: "mgmt.sys.welcome.desc" },
  { id: "onboarding_tip", icon: "🚀", baslik: t("mgmt.sys.onboarding_tip.title"), aciklama: t("mgmt.sys.onboarding_tip.desc"), baslikKey: "mgmt.sys.onboarding_tip.title", aciklamaKey: "mgmt.sys.onboarding_tip.desc" },
  { id: "community", icon: "🤝", baslik: t("mgmt.sys.community.title"), aciklama: t("mgmt.sys.community.desc"), baslikKey: "mgmt.sys.community.title", aciklamaKey: "mgmt.sys.community.desc" },
  { id: "known_issue", icon: "🐞", baslik: t("mgmt.sys.known_issue.title"), aciklama: t("mgmt.sys.known_issue.desc"), baslikKey: "mgmt.sys.known_issue.title", aciklamaKey: "mgmt.sys.known_issue.desc" },
  { id: "issue_resolved", icon: "✅", baslik: t("mgmt.sys.issue_resolved.title"), aciklama: t("mgmt.sys.issue_resolved.desc"), baslikKey: "mgmt.sys.issue_resolved.title", aciklamaKey: "mgmt.sys.issue_resolved.desc" },
  { id: "server_migration", icon: "🔄", baslik: t("mgmt.sys.server_migration.title"), aciklama: t("mgmt.sys.server_migration.desc"), baslikKey: "mgmt.sys.server_migration.title", aciklamaKey: "mgmt.sys.server_migration.desc" },
  { id: "new_app_version", icon: "📱 ", baslik: t("mgmt.sys.new_app_version.title"), aciklama: t("mgmt.sys.new_app_version.desc"), baslikKey: "mgmt.sys.new_app_version.title", aciklamaKey: "mgmt.sys.new_app_version.desc" },
  { id: "storage_warning", icon: "📦", baslik: t("mgmt.sys.storage_warning.title"), aciklama: t("mgmt.sys.storage_warning.desc"), baslikKey: "mgmt.sys.storage_warning.title", aciklamaKey: "mgmt.sys.storage_warning.desc" },
  { id: "privacy_update", icon: "🔒", baslik: t("mgmt.sys.privacy_update.title"), aciklama: t("mgmt.sys.privacy_update.desc"), baslikKey: "mgmt.sys.privacy_update.title", aciklamaKey: "mgmt.sys.privacy_update.desc" },
  { id: "terms_update", icon: "📄", baslik: t("mgmt.sys.terms_update.title"), aciklama: t("mgmt.sys.terms_update.desc"), baslikKey: "mgmt.sys.terms_update.title", aciklamaKey: "mgmt.sys.terms_update.desc" },
  { id: "holiday_closure", icon: "🏖️", baslik: t("mgmt.sys.holiday_closure.title"), aciklama: t("mgmt.sys.holiday_closure.desc"), baslikKey: "mgmt.sys.holiday_closure.title", aciklamaKey: "mgmt.sys.holiday_closure.desc" },
  { id: "weather_alert", icon: "🌦️", baslik: t("mgmt.sys.weather_alert.title"), aciklama: t("mgmt.sys.weather_alert.desc"), baslikKey: "mgmt.sys.weather_alert.title", aciklamaKey: "mgmt.sys.weather_alert.desc" },
  { id: "family_milestone", icon: "🏆", baslik: t("mgmt.sys.family_milestone.title"), aciklama: t("mgmt.sys.family_milestone.desc"), baslikKey: "mgmt.sys.family_milestone.title", aciklamaKey: "mgmt.sys.family_milestone.desc" },
  { id: "invite_friends", icon: "📨", baslik: t("mgmt.sys.invite_friends.title"), aciklama: t("mgmt.sys.invite_friends.desc"), baslikKey: "mgmt.sys.invite_friends.title", aciklamaKey: "mgmt.sys.invite_friends.desc" },
  { id: "rate_app", icon: "⭐", baslik: t("mgmt.sys.rate_app.title"), aciklama: t("mgmt.sys.rate_app.desc"), baslikKey: "mgmt.sys.rate_app.title", aciklamaKey: "mgmt.sys.rate_app.desc" },
  { id: "new_integration", icon: "🔗", baslik: t("mgmt.sys.new_integration.title"), aciklama: t("mgmt.sys.new_integration.desc"), baslikKey: "mgmt.sys.new_integration.title", aciklamaKey: "mgmt.sys.new_integration.desc" },
  { id: "seasonal_tip", icon: "🍂", baslik: t("mgmt.sys.seasonal_tip.title"), aciklama: t("mgmt.sys.seasonal_tip.desc"), baslikKey: "mgmt.sys.seasonal_tip.title", aciklamaKey: "mgmt.sys.seasonal_tip.desc" },
  { id: "back_to_school", icon: "🎒", baslik: t("mgmt.sys.back_to_school.title"), aciklama: t("mgmt.sys.back_to_school.desc"), baslikKey: "mgmt.sys.back_to_school.title", aciklamaKey: "mgmt.sys.back_to_school.desc" },
  { id: "daylight_saving", icon: "🕐", baslik: t("mgmt.sys.daylight_saving.title"), aciklama: t("mgmt.sys.daylight_saving.desc"), baslikKey: "mgmt.sys.daylight_saving.title", aciklamaKey: "mgmt.sys.daylight_saving.desc" },
  { id: "subscription_expired", icon: "⏰", baslik: t("mgmt.sys.subscription_expired.title"), aciklama: t("mgmt.sys.subscription_expired.desc"), baslikKey: "mgmt.sys.subscription_expired.title", aciklamaKey: "mgmt.sys.subscription_expired.desc" },
  { id: "payment_failed", icon: "⚠️", baslik: t("mgmt.sys.payment_failed.title"), aciklama: t("mgmt.sys.payment_failed.desc"), baslikKey: "mgmt.sys.payment_failed.title", aciklamaKey: "mgmt.sys.payment_failed.desc" },
  { id: "new_help_center", icon: "❓", baslik: t("mgmt.sys.new_help_center.title"), aciklama: t("mgmt.sys.new_help_center.desc"), baslikKey: "mgmt.sys.new_help_center.title", aciklamaKey: "mgmt.sys.new_help_center.desc" },
  { id: "data_export_ready", icon: "📤", baslik: t("mgmt.sys.data_export_ready.title"), aciklama: t("mgmt.sys.data_export_ready.desc"), baslikKey: "mgmt.sys.data_export_ready.title", aciklamaKey: "mgmt.sys.data_export_ready.desc" },
  { id: "general_thanks_loyal", icon: "🙏", baslik: t("mgmt.sys.general_thanks_loyal.title"), aciklama: t("mgmt.sys.general_thanks_loyal.desc"), baslikKey: "mgmt.sys.general_thanks_loyal.title", aciklamaKey: "mgmt.sys.general_thanks_loyal.desc" },
];

const getSistemDuyuruTekrarTipleri = (t) => [
  { value: "none", label: t("mgmt.repeat.once") },
  { value: "hourly", label: "Her Saat" },
  { value: "daily", label: t("mgmt.repeat.daily") },
  { value: "weekly", label: "Her Hafta" },
  { value: "monthly", label: "Her Ay" },
];

const getZamanlanmisBildirimFiltreleri = (t) => [
  { value: "all", label: t("common.all") },
  { value: "once", label: "Tek Seferlik" },
  { value: "repeat", label: t("mgmt.notify.repeat") },
  { value: "system", label: "Sistem Duyurusu" },
  { value: "family", label: t("mgmt.target.familyUsers") },
  { value: "users", label: t("mgmt.notif.target") },
];

const ZAMANLANMIS_TEKRAR_KEYS = {
  none: "mgmt.repeat.none",
  hourly: "mgmt.repeat.hourly",
  daily: "mgmt.repeat.daily",
  every3days: "mgmt.repeat.every3days",
  weekly: "mgmt.repeat.weekly",
  monthly: "mgmt.repeat.monthly",
};

const getOtomatikKuralOptionLabels = (t) => ({
  onlyIfPendingItems: t("mgmt.auto.onlyPending"),
  oncePerDay: t("mgmt.auto.oncePerDay"),
  today: t("mgmt.auto.dueToday"),
  tomorrow: t("mgmt.auto.dueTomorrow"),
  threeDaysBefore: t("mgmt.auto.threeDays"),
  overdue: "Gecikenler",
  thisWeek: "Bu haftaki etkinlikler",
  thirtyDays: t("mgmt.auto.thirtyDays"),
  tenDays: t("mgmt.auto.tenDays"),
  fiveDays: t("mgmt.auto.fiveDays"),
  expired: t("mgmt.auto.expired"),
  appointments: "Randevular",
  medicines: t("mgmt.auto.medicines"),
});

const policyRuleValue = (rule, field, fallback = "") => {
  if (!rule) return fallback;
  return rule[field] ?? fallback;
};

const currentTimeForInput = () => {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

const policyRuleSubText = (rule = {}, t) => {
  if (rule.usesOwnTime) {
    return `Kendi saatinde · Erteleme: ${rule.snoozeMinutes || 10} dk · Maks tekrar: ${rule.maxRepeat || 3}`;
  }
  if (typeof rule.daysBefore === "number") {
    return rule.daysBefore === 0 ? t("mgmt.rule.sameDay") : t("mgmt.rule.daysBefore", { count: rule.daysBefore });
  }
  if (rule.type === "overdue") return t("mgmt.rule.overdue");
  if (rule.type === "expired") return t("mgmt.rule.expired");
  if (rule.type === "weekly") return t("mgmt.rule.weekly");
  return t("mgmt.auto.timepolicy");
};

const otomasyonTarihFormatla = (value) => {
  if (!value) return "-";
  try {
    const d = value?.toDate
      ? value.toDate()
      : Number.isFinite(Number(value?._seconds ?? value?.seconds))
        ? new Date(Number(value?._seconds ?? value?.seconds) * 1000)
        : new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const familyNotificationDate = (value, lang) => {
  if (!value) return "";
  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(lang === "en" ? "en-US" : "tr-TR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const datetimeLocalValue = (value) => {
  if (!value) return "";
  try {
    const d = value?.toDate
      ? value.toDate()
      : Number.isFinite(Number(value?._seconds ?? value?.seconds))
        ? new Date(Number(value?._seconds ?? value?.seconds) * 1000)
        : new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
};

const hedefSayisiBul = (item) => {
  if (typeof item?.hedefSayisi === "number") return item.hedefSayisi;
  if (Array.isArray(item?.hedefUidler)) return item.hedefUidler.length;
  return 0;
};

const hedefTipiLabel = (item, t) => {
  const _t = t || ((k) => k);
  if (item?.hedefTipi === "allUsers") return _t("mgmt.target.allUsers");
  if (item?.hedefTipi === "familyUsers") return _t("mgmt.target.familyUsers");
  if (item?.hedefTipi === "users") return _t("mgmt.target.users");
  return item?.hedefTipi || "-";
};

const zamanlanmisFiltreUygunMu = (item, filtre) => {
  if (filtre === "all") return true;
  if (filtre === "once") return !item.tekrarTipi || item.tekrarTipi === "none";
  if (filtre === "repeat") return item.tekrarTipi && item.tekrarTipi !== "none";
  if (filtre === "system") return item.hedefTipi === "allUsers" || String(item.tip || "").includes("system");
  if (filtre === "family") return item.hedefTipi === "familyUsers" || item.tip === "familyScheduled";
  if (filtre === "users") return item.hedefTipi === "users";
  return true;
};

const MANAGEMENT_LABELS = {
  "Baba": "Father",
  "Anne": "Mother",
  "Çocuk": "Child",
  "Bebek": "Baby",
  "Dede": "Grandfather",
  "Nene": "Grandmother",
  "Amca": "Uncle",
  "Hala": "Aunt",
  "Teyze": "Aunt",
  "Dayı": "Uncle",
  "Evcil Hayvan": "Pet",
  "Kişisel": "Personal",
  "Diğer": "Other",
  "Aile Yöneticisi": "Family Admin",
  "Aile Üyesi": "Family Member",
  "Aile üyesi": "Family Member",
  "aile üyesi": "family member",
  "Kurucu": "Founder",
  "Yenile": "Refresh",
  "Kim Okudu": "Who Read",
  "Okunma Yenile": "Refresh Reads",
  "Kapat": "Close",
};
const mLabel = (lang, value, fallback = "") => {
  const raw = value || fallback || "";
  return lang === "en" ? (MANAGEMENT_LABELS[raw] || raw) : raw;
};
const memberRoleLine = (lang, member, t) =>
  `${mLabel(lang, member?.rol || "", "-")} · ${mLabel(lang, member?.aileYetkisi || "", t("mgmt.notif.familyMember"))}`;

const FAMILY_PROFILE_ROLE_ORDER = ["Kişisel", "Baba", "Anne", "Çocuk", "Amca", "Hala", "Teyze", "Dayı", "Dede", "Nene", "Diğer"];
const FAMILY_PROFILE_ROLE_OPTIONS = ["Kişisel", "Baba", "Anne", "Çocuk", "Amca", "Hala", "Teyze", "Dayı", "Dede", "Nene", "Diğer", "Bebek", "Evcil Hayvan"];
const FAMILY_MEMBERSHIP_OPTIONS = ["Aile Yöneticisi", "Aile Üyesi"];

const familyRoleRank = (role) => {
  const idx = FAMILY_PROFILE_ROLE_ORDER.indexOf(String(role || ""));
  return idx === -1 ? FAMILY_PROFILE_ROLE_ORDER.length + 1 : idx;
};

const sortFamilyMembersForManagement = (members = []) =>
  [...members].sort((a, b) => {
    const roleDiff = familyRoleRank(a?.rol || a?.profilRol) - familyRoleRank(b?.rol || b?.profilRol);
    if (roleDiff !== 0) return roleDiff;
    const nameA = `${a?.ad || ""} ${a?.soyad || ""}`.trim() || a?.displayName || a?.email || "";
    const nameB = `${b?.ad || ""} ${b?.soyad || ""}`.trim() || b?.displayName || b?.email || "";
    return nameA.localeCompare(nameB, "tr");
  });

/* ─────────────────────────────────────────
   ANA SAYFA
───────────────────────────────────────── */
function UserManagementPage({
  uyeler,
  seciliYetkiUye,
  setSeciliYetkiUye,
  yetkiKaydet,
  uyeSifreSifirla,
  uyeYetkiDegistir,
  uyeRolDegistir,
  profil,
  permissions,
  yoneticiMi = false,
  premiumConfig: initialPremiumConfig = {},
}) {
  const { t, lang } = useLanguage();
  const tr = lang !== "en";


  const SUPER_ADMIN_UID = "eRmq1zfRhKM4LH2SrGG8G1Q5tEG2";

const superAdminMi =
  profil?.systemRole === "superAdmin" ||
  profil?.uid === SUPER_ADMIN_UID;

const bildirimYonetebilir = superAdminMi;

  const uyeYonetebilir = yoneticiMi || superAdminMi || permissions?.actions?.manageUsers === true;

  const aileBildirimYonetebilir =
  yoneticiMi || superAdminMi || permissions?.actions?.sendFamilyNotification === true;

  /* ── Premium panel state ── */
  const [premiumPanelAcik, setPremiumPanelAcik] = useState(false);
  const [premiumKaydediliyor, setPremiumKaydediliyor] = useState(false);
  const [premiumKayitMesaj, setPremiumKayitMesaj] = useState("");
  const [localPremiumConfig, setLocalPremiumConfig] = useState(() => ({
    premiumEnabled: initialPremiumConfig?.premiumEnabled !== false,
    limits: { ...DEFAULT_PREMIUM_CONFIG.limits, ...(initialPremiumConfig?.limits || {}) },
    lockedFeatures: { ...DEFAULT_PREMIUM_CONFIG.lockedFeatures, ...(initialPremiumConfig?.lockedFeatures || {}) },
  }));

  useEffect(() => {
    if (initialPremiumConfig && (initialPremiumConfig.limits || initialPremiumConfig.lockedFeatures || initialPremiumConfig.premiumEnabled !== undefined)) {
      setLocalPremiumConfig({
        premiumEnabled: initialPremiumConfig.premiumEnabled !== false,
        limits: { ...DEFAULT_PREMIUM_CONFIG.limits, ...(initialPremiumConfig.limits || {}) },
        lockedFeatures: { ...DEFAULT_PREMIUM_CONFIG.lockedFeatures, ...(initialPremiumConfig.lockedFeatures || {}) },
      });
    }
  }, [JSON.stringify(initialPremiumConfig)]); // eslint-disable-line

  const setLimit = (key, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    setLocalPremiumConfig(prev => ({ ...prev, limits: { ...prev.limits, [key]: num } }));
  };
  const setLocked = (key, value) => {
    setLocalPremiumConfig(prev => ({ ...prev, lockedFeatures: { ...prev.lockedFeatures, [key]: value } }));
  };
  const setPremiumEnabled = (value) => {
    setLocalPremiumConfig(prev => ({ ...prev, premiumEnabled: value }));
  };
  const premiumKaydet = async () => {
    setPremiumKaydediliyor(true);
    setPremiumKayitMesaj("");
    try {
      await savePremiumConfig(localPremiumConfig);
      setPremiumKayitMesaj(t("mgmt.premium.savedOk"));
    } catch (e) {
      setPremiumKayitMesaj(`${t("mgmt.premium.saveError")}: ${e.message}`);
    } finally {
      setPremiumKaydediliyor(false);
      setTimeout(() => setPremiumKayitMesaj(""), 3000);
    }
  };
  const premiumVarsayilanaYukle = () => {
    setLocalPremiumConfig({
      premiumEnabled: true,
      limits: { ...DEFAULT_PREMIUM_CONFIG.limits },
      lockedFeatures: { ...DEFAULT_PREMIUM_CONFIG.lockedFeatures },
    });
    setPremiumKayitMesaj(t("mgmt.premium.defaultsLoaded"));
    setTimeout(() => setPremiumKayitMesaj(""), 3000);
  };
  const [testBildirimDurum, setTestBildirimDurum] = useState("");
const [testBildirimGonderiliyor, setTestBildirimGonderiliyor] = useState(false);
const [hatirlatmaMerkeziAcik, setHatirlatmaMerkeziAcik] = useState(false);
const [magazaDegerlendirmePaneliAcik, setMagazaDegerlendirmePaneliAcik] = useState(false);
const [mailTestPaneliAcik, setMailTestPaneliAcik] = useState(false);
const [aiBildirimPaneliAcik, setAiBildirimPaneliAcik] = useState(false);
const [canliAktivitePaneliAcik, setCanliAktivitePaneliAcik] = useState(false);
const [canliAktifKullaniciSayisi, setCanliAktifKullaniciSayisi] = useState(null);
const [siteTrafficPaneliAcik, setSiteTrafficPaneliAcik] = useState(false);
const [bildirimYonetimiPaneliAcik, setBildirimYonetimiPaneliAcik] = useState(false);
const [bildirimGecmisiPaneliAcik, setBildirimGecmisiPaneliAcik] = useState(false);
const [hesapSilmeAnketiPaneliAcik, setHesapSilmeAnketiPaneliAcik] = useState(false);
const [operasyonMerkeziAcik, setOperasyonMerkeziAcik] = useState(false);
const [globalKullanicilar, setGlobalKullanicilar] = useState([]);
const [adminMesajSablonlari, setAdminMesajSablonlari] = useState(null);
const [duzenlenenKullaniciSablonId, setDuzenlenenKullaniciSablonId] = useState(null);
const [duzenlenenSistemSablonId, setDuzenlenenSistemSablonId] = useState(null);
const [sistemDuyuruDilFiltresi, setSistemDuyuruDilFiltresi] = useState("all");
const [sistemDuyuruPlatformFiltresi, setSistemDuyuruPlatformFiltresi] = useState("all");
const [sistemDuyuruPanelAcik, setSistemDuyuruPanelAcik] = useState(false);
const [sistemDuyuruBaslik, setSistemDuyuruBaslik] = useState("");
const [sistemDuyuruMesaj, setSistemDuyuruMesaj] = useState("");
// Hazır şablon seçildiyse buradaki key'ler dolu olur — gönderim sırasında
// render edilmiş metin yerine bu key'ler backend'e gider, böylece her kullanıcı
// kendi cihaz diline göre çevrilmiş halini görür. Admin metni ELLE değiştirirse
// (handleSistemDuyuruBaslikChange / handleSistemDuyuruMesajChange) bu key'ler
// temizlenir ve serbest-metin (TR/EN ikili giriş) moduna geçilir.
const [sistemDuyuruSecilenSablon, setSistemDuyuruSecilenSablon] = useState(null); // { baslikKey, aciklamaKey }
const [sistemDuyuruBaslikEn, setSistemDuyuruBaslikEn] = useState("");
const [sistemDuyuruMesajEn, setSistemDuyuruMesajEn] = useState("");
const [sistemDuyuruModu, setSistemDuyuruModu] = useState("now");
const [sistemDuyuruPlanlananTarih, setSistemDuyuruPlanlananTarih] = useState("");
const [sistemDuyuruTekrarTipi, setSistemDuyuruTekrarTipi] = useState("none");
const [sistemDuyuruDurum, setSistemDuyuruDurum] = useState("");
const [sistemDuyuruGonderiliyor, setSistemDuyuruGonderiliyor] = useState(false);
const [kullaniciBildirimAcik, setKullaniciBildirimAcik] = useState(false);
const [kullaniciArama, setKullaniciArama] = useState("");
const [kullaniciFiltrePaneliAcik, setKullaniciFiltrePaneliAcik] = useState(false);
const [kullaniciSiralamasi, setKullaniciSiralamasi] = useState("newest");
const [kullaniciPlatformFiltresi, setKullaniciPlatformFiltresi] = useState("all");
const [kullaniciKanalFiltresi, setKullaniciKanalFiltresi] = useState("all");
const [kullaniciDilFiltresi, setKullaniciDilFiltresi] = useState("all");
const [kullaniciSurumFiltresi, setKullaniciSurumFiltresi] = useState("all");
const [kullaniciAktiviteFiltresi, setKullaniciAktiviteFiltresi] = useState("all");
const [kullaniciPremiumFiltresi, setKullaniciPremiumFiltresi] = useState("all");
const [globalKullaniciSonYenileme, setGlobalKullaniciSonYenileme] = useState(null);
const [globalKullanicilarYukleniyor, setGlobalKullanicilarYukleniyor] = useState(false);
const [seciliUidler, setSeciliUidler] = useState([]);
const [kullaniciBaslik, setKullaniciBaslik] = useState("");
const [kullaniciMesaj, setKullaniciMesaj] = useState("");
const [kullaniciBildirimDurum, setKullaniciBildirimDurum] = useState("");
const [kullaniciBildirimGonderiliyor, setKullaniciBildirimGonderiliyor] = useState(false);
const [bildirimModu, setBildirimModu] = useState("now");
const [acikBildirimAliciId, setAcikBildirimAliciId] = useState(null);
const [bildirimAlicilari, setBildirimAlicilari] = useState({});
const [bildirimAlicilariYukleniyor, setBildirimAlicilariYukleniyor] = useState(null);

useEffect(() => {
  if (!superAdminMi) return;
  adminBildirimSablonlariniGetir().then((templates) => {
    if (templates && typeof templates === "object") setAdminMesajSablonlari(templates);
  }).catch(() => {});
}, [superAdminMi]);

const kullaniciHazirMesajlari = adminMesajSablonlari?.userTemplates || getHazirBildirimMesajlari(t);
const sistemHazirMesajlari = adminMesajSablonlari?.systemTemplates || getHazirSistemDuyuruKartlari(t);
const sablonListesiKaydet = async (alan, liste) => {
  const next = {
    userTemplates: adminMesajSablonlari?.userTemplates || getHazirBildirimMesajlari(t),
    systemTemplates: adminMesajSablonlari?.systemTemplates || getHazirSistemDuyuruKartlari(t),
    [alan]: liste,
  };
  setAdminMesajSablonlari(next);
  try { setAdminMesajSablonlari(await adminBildirimSablonlariniKaydet(next)); }
  catch (error) { appAlert(error?.message || (tr ? "Hazır mesaj kaydedilemedi." : "Template could not be saved.")); }
};
const gecerliSablonOlustur = (baslik, aciklama, mevcut = null) => ({
  id: `custom-${Date.now()}`,
  icon: mevcut?.icon || "✦",
  baslik: String(baslik || "").trim().replace(mevcut?.icon ? new RegExp(`^${mevcut.icon}\\s*`) : /^$/, ""),
  aciklama: String(aciklama || "").trim(),
});

const [planlananTarih, setPlanlananTarih] = useState("");

const [tekrarTipi, setTekrarTipi] = useState("none");
const [gecmisAcik, setGecmisAcik] = useState(false);
// Bildirim geçmişi iki ayrı sekmede gösterilir: admin/sistem duyuruları ve
// otomatik (cron) bildirim kontrolleri. Backend zaten her kaydın "tip" (module)
// alanını döndürüyor — "automation" olanlar otomasyon sekmesine, diğerleri
// (adminNotification/systemAnnouncement/familyAnnouncement/scheduled*) duyuru
// sekmesine ayrılır.
const [gecmisSekme, setGecmisSekme] = useState("duyurular"); // "duyurular" | "otomasyon"
const [bildirimGecmisi, setBildirimGecmisi] = useState([]);
const [gecmisYukleniyor, setGecmisYukleniyor] = useState(false);

const [aileBildirimPanelAcik, setAileBildirimPanelAcik] = useState(false);
const [aileBildirimArama, setAileBildirimArama] = useState("");
const [aileSeciliUidler, setAileSeciliUidler] = useState([]);
const [aileBildirimBaslik, setAileBildirimBaslik] = useState("");
const [aileBildirimMesaj, setAileBildirimMesaj] = useState("");
const [aileGonderimTuru, setAileGonderimTuru] = useState("normal");
const [aileBildirimModu, setAileBildirimModu] = useState("now");
const [ailePlanlananTarih, setAilePlanlananTarih] = useState("");
const [aileTekrarTipi, setAileTekrarTipi] = useState("none");
const [aileBildirimDurum, setAileBildirimDurum] = useState("");
const [aileBildirimGonderiliyor, setAileBildirimGonderiliyor] = useState(false);
const [sonAileBildirimleri, setSonAileBildirimleri] = useState([]);
const [sonAileBildirimPanelAcik, setSonAileBildirimPanelAcik] = useState(false);
const [zamanliAileBildirimleri, setZamanliAileBildirimleri] = useState([]);
const [zamanliAilePanelAcik, setZamanliAilePanelAcik] = useState(false);
const [okunmaDetaylari, setOkunmaDetaylari] = useState({});
const [otomatikKurallarAcik, setOtomatikKurallarAcik] = useState(false);
const [otomatikKurallar, setOtomatikKurallar] = useState(DEFAULT_AUTOMATION_RULES);
const [otomatikLoglar, setOtomatikLoglar] = useState([]);
const [otomatikTekrarDuyurular, setOtomatikTekrarDuyurular] = useState([]);
const [otomatikSonCalisma, setOtomatikSonCalisma] = useState(null);
const [otomatikDurum, setOtomatikDurum] = useState("");
const [otomatikYukleniyor, setOtomatikYukleniyor] = useState(false);
const [otomatikSimdiCalisiyor, setOtomatikSimdiCalisiyor] = useState(false);
const [otomatikLogDetayId, setOtomatikLogDetayId] = useState(null);
const [otomatikLogDetaylari, setOtomatikLogDetaylari] = useState({});
const [otomatikLogDetayYukleniyor, setOtomatikLogDetayYukleniyor] = useState(null);
const [son24SaatDetayAcik, setSon24SaatDetayAcik] = useState(false);
const [son24SaatPushlari, setSon24SaatPushlari] = useState({ items: [], total: 0 });
const [son24SaatPushYukleniyor, setSon24SaatPushYukleniyor] = useState(false);
const [bildirimOtomasyonPanelAcik, setBildirimOtomasyonPanelAcik] = useState(false);
const [bildirimPolitikaGlobal, setBildirimPolitikaGlobal] = useState(DEFAULT_NOTIFICATION_POLICY_GLOBAL);
const [bildirimPolitikalari, setBildirimPolitikalari] = useState(DEFAULT_NOTIFICATION_POLICIES);
const [bildirimPolitikaAktifKey, setBildirimPolitikaAktifKey] = useState(DEFAULT_NOTIFICATION_POLICIES[0]?.key || "shoppingReminder");
const [bildirimOtomasyonOzet, setBildirimOtomasyonOzet] = useState({ aktifZamanli: 0, bekleyen: 0, bugunGonderilen: 0, son24SaatGonderilen: 0, son24SaatHata: 0, sonCalisma: null });
const [bildirimOtomasyonDurum, setBildirimOtomasyonDurum] = useState("");
const [bildirimOtomasyonYukleniyor, setBildirimOtomasyonYukleniyor] = useState(false);
const [bildirimOtomasyonKaydediliyor, setBildirimOtomasyonKaydediliyor] = useState("");
const [zamanlanmisAdminPanelAcik, setZamanlanmisAdminPanelAcik] = useState(false);
const [zamanlanmisBildirimler, setZamanlanmisBildirimler] = useState([]);
const [zamanlanmisFiltre, setZamanlanmisFiltre] = useState("all");
const [zamanlanmisDurum, setZamanlanmisDurum] = useState("");
const [zamanlanmisYukleniyor, setZamanlanmisYukleniyor] = useState(false);
const [zamanlanmisAcikId, setZamanlanmisAcikId] = useState(null);
const [zamanlanmisGosterimAdedi, setZamanlanmisGosterimAdedi] = useState(10);
const zamanlanmisBildirimleriYukle = async ({ panelAc = true } = {}) => {
  try {
    setZamanlanmisYukleniyor(true);
    setZamanlanmisDurum("");
    const liste = await bekleyenZamanlanmisBildirimleriGetir();
    setZamanlanmisBildirimler(liste);
    if (panelAc) setZamanlanmisAdminPanelAcik(true);
  } catch (err) {
    console.error("Zamanlanmış bildirimler yüklenemedi:", err);
    setZamanlanmisDurum(t("mgmt.sched.loadFailed"));
  } finally {
    setZamanlanmisYukleniyor(false);
  }
};

const zamanlanmisAdminPanelToggle = () => {
  if (zamanlanmisAdminPanelAcik) {
    setZamanlanmisAdminPanelAcik(false);
  } else {
    zamanlanmisBildirimleriYukle();
  }
};

const zamanlanmisBildirimLocalGuncelle = (id, alan, deger) => {
  setZamanlanmisBildirimler((prev) =>
    prev.map((item) => (item.id === id ? { ...item, [alan]: deger } : item))
  );
};

const zamanlanmisBildirimiKaydetClick = async (item) => {
  try {
    if (!item?.id) return;
    if (!String(item.baslik || "").trim() || !String(item.aciklama || "").trim()) {
      appAlert(t("mgmt.notif.titleRequired"));
      return;
    }
    if (!item.planlananTarih) {
      appAlert(t("mgmt.error.scheduledDateRequired"));
      return;
    }

    await zamanlanmisBildirimiGuncelle(item.id, {
      baslik: String(item.baslik || "").trim(),
      aciklama: String(item.aciklama || "").trim(),
      planlananTarih: item.planlananTarih,
      tekrarTipi: item.tekrarTipi || "none",
    });

    setZamanlanmisDurum(t("mgmt.sched.updatedOk"));
    await zamanlanmisBildirimleriYukle({ panelAc: false });
  } catch (err) {
    console.error("Zamanlanmış bildirim güncellenemedi:", err);
    setZamanlanmisDurum(err?.message || t("mgmt.sched.updateFailed"));
  }
};

const zamanlanmisBildirimiIptalClick = async (item) => {
  const onay = await appConfirm(t("mgmt.sched.confirmCancel"));
  if (!onay) return;

  try {
    await zamanlanmisBildirimiIptalEt(item.id);
    setZamanlanmisDurum(t("mgmt.sched.cancelledOk"));
    await zamanlanmisBildirimleriYukle({ panelAc: false });
  } catch (err) {
    console.error("Zamanlanmış bildirim iptal edilemedi:", err);
    setZamanlanmisDurum(err?.message || t("mgmt.sched.cancelFailed"));
  }
};

const zamanlanmisBildirimiSilClick = async (item) => {
  const onay = await appConfirm(t("mgmt.sched.confirmDelete"));
  if (!onay) return;

  try {
    await zamanlanmisBildirimiSil(item.id);
    setZamanlanmisDurum(t("mgmt.sched.deletedOk"));
    await zamanlanmisBildirimleriYukle({ panelAc: false });
  } catch (err) {
    console.error("Zamanlanmış bildirim silinemedi:", err);
    setZamanlanmisDurum(err?.message || t("mgmt.sched.deleteFailed"));
  }
};

const filtreliZamanlanmisBildirimler = zamanlanmisBildirimler.filter((item) =>
  zamanlanmisFiltreUygunMu(item, zamanlanmisFiltre)
);
const gorunenZamanlanmisBildirimler = filtreliZamanlanmisBildirimler.slice(0, zamanlanmisGosterimAdedi);
const dahaFazlaZamanlanmisVar = filtreliZamanlanmisBildirimler.length > gorunenZamanlanmisBildirimler.length;

const otomatikKurallariYukle = async ({ panelAc = true } = {}) => {
  try {
    setOtomatikYukleniyor(true);
    setOtomatikDurum("");

    const [kurallar, durum, tekrarDuyurular] = await Promise.all([
      otomatikBildirimKurallariniGetir(),
      otomatikBildirimDurumunuGetir(),
      tekrarEdenSistemDuyurulariniGetir(),
    ]);

    setOtomatikKurallar(kurallar);
    setOtomatikLoglar(durum.logs || []);
    setOtomatikTekrarDuyurular(tekrarDuyurular || []);
    setOtomatikSonCalisma(durum.sonCalisma || null);
    setBildirimOtomasyonOzet((prev) => ({ ...prev, ...(durum.ozet || {}) }));
    if (panelAc) setOtomatikKurallarAcik(true);
  } catch (err) {
    console.error("Otomatik bildirim kuralları yüklenemedi:", err);
    setOtomatikDurum(t("mgmt.auto.loadFailed"));
  } finally {
    setOtomatikYukleniyor(false);
  }
};

const otomatikLogDetayToggle = async (log) => {
  if (!log?.id || Number(log.totalSent || 0) < 1) return;
  if (otomatikLogDetayId === log.id) {
    setOtomatikLogDetayId(null);
    return;
  }
  setOtomatikLogDetayId(log.id);
  if (otomatikLogDetaylari[log.id]) return;
  try {
    setOtomatikLogDetayYukleniyor(log.id);
    const items = await otomatikBildirimGonderimleriniGetir(log.id);
    setOtomatikLogDetaylari((prev) => ({ ...prev, [log.id]: items }));
  } catch (err) {
    console.error("Otomasyon gönderim ayrıntıları yüklenemedi:", err);
    setOtomatikLogDetaylari((prev) => ({ ...prev, [log.id]: [] }));
  } finally {
    setOtomatikLogDetayYukleniyor(null);
  }
};

const son24SaatPushDetayToggle = async () => {
  if (son24SaatDetayAcik) {
    setSon24SaatDetayAcik(false);
    return;
  }
  setSon24SaatDetayAcik(true);
  if (son24SaatPushlari.items.length) return;
  try {
    setSon24SaatPushYukleniyor(true);
    setSon24SaatPushlari(await sonSaatlerdekiPushlariGetir(24, 0, 100));
  } catch (err) {
    console.error("24 saatlik push ayrıntıları yüklenemedi:", err);
    setSon24SaatPushlari({ items: [], total: 0 });
  } finally { setSon24SaatPushYukleniyor(false); }
};

const dahaFazlaSon24SaatPushYukle = async () => {
  if (son24SaatPushYukleniyor || son24SaatPushlari.items.length >= son24SaatPushlari.total) return;
  try {
    setSon24SaatPushYukleniyor(true);
    const result = await sonSaatlerdekiPushlariGetir(24, son24SaatPushlari.items.length, 100);
    setSon24SaatPushlari((current) => ({ items: [...current.items, ...result.items], total: result.total }));
  } finally { setSon24SaatPushYukleniyor(false); }
};

const otomatikKuralToggle = async (rule) => {
  try {
    const enabled = !rule.enabled;
    setOtomatikKurallar((prev) =>
      prev.map((r) => (r.key === rule.key ? { ...r, enabled } : r))
    );

    await otomatikBildirimKuraliniKaydet(rule.key, {
      ...rule,
      enabled,
    });
  } catch (err) {
    console.error("Otomatik kural güncellenemedi:", err);
    setOtomatikDurum(t("mgmt.auto.ruleFailed"));
    await otomatikKurallariYukle({ panelAc: false });
  }
};

const otomatikKuralOptionToggle = async (rule, optionKey) => {
  try {
    const yeniOptions = {
      ...(rule.options || {}),
      [optionKey]: !(rule.options || {})[optionKey],
    };

    setOtomatikKurallar((prev) =>
      prev.map((r) => (r.key === rule.key ? { ...r, options: yeniOptions } : r))
    );

    await otomatikBildirimKuraliniKaydet(rule.key, {
      ...rule,
      options: yeniOptions,
    });
  } catch (err) {
    console.error("Otomatik kural seçeneği güncellenemedi:", err);
    setOtomatikDurum(t("mgmt.auto.optionFailed"));
    await otomatikKurallariYukle({ panelAc: false });
  }
};

const otomatikBildirimleriSimdiKontrolEt = async () => {
  try {
    setOtomatikSimdiCalisiyor(true);
    setOtomatikDurum("");
    const sonuc = await otomatikBildirimleriSimdiCalistir();
    setOtomatikDurum(
      t("mgmt.auto.runResult", { created: sonuc?.totalCreated ?? 0, sent: sonuc?.totalSent ?? 0, failed: sonuc?.totalFailed ?? 0 })
    );
    await otomatikKurallariYukle({ panelAc: false });
  } catch (err) {
    console.error("Otomatik bildirimler çalıştırılamadı:", err);
    setOtomatikDurum(err.message || t("mgmt.auto.runFailed"));
  } finally {
    setOtomatikSimdiCalisiyor(false);
  }
};

const tekrarEdenSistemDuyurusuIptalClick = async (item) => {
  const onay = await appConfirm(t("mgmt.sched.confirmCancelRecurring"));
  if (!onay) return;

  try {
    await tekrarEdenSistemDuyurusunuIptalEt(item.id);
    await otomatikKurallariYukle({ panelAc: false });
  } catch (err) {
    console.error("Tekrar eden sistem duyurusu iptal edilemedi:", err);
    setOtomatikDurum("Tekrar eden sistem duyurusu iptal edilemedi.");
  }
};

const tekrarEdenSistemDuyurusuDurumToggle = async (item) => {
  try {
    const yeniDurum = item.durum === "duraklatildi" ? "bekliyor" : "duraklatildi";
    await tekrarEdenSistemDuyurusunuGuncelle(item.id, { durum: yeniDurum });
    await otomatikKurallariYukle({ panelAc: false });
  } catch (err) {
    console.error("Tekrar eden sistem duyurusu güncellenemedi:", err);
    setOtomatikDurum(t("mgmt.auto.repeatFailed"));
  }
};

const otomatikKurallarPanelToggle = () => {
  if (otomatikKurallarAcik) {
    setOtomatikKurallarAcik(false);
  } else {
    otomatikKurallariYukle();
  }
};


const bildirimOtomasyonunuYukle = async ({ panelAc = true } = {}) => {
  try {
    setBildirimOtomasyonYukleniyor(true);
    setBildirimOtomasyonDurum("");

    const [policyData, ozet] = await Promise.all([
      bildirimPolitikalariGetir(),
      bildirimOtomasyonOzetiniGetir(),
    ]);

    setBildirimPolitikaGlobal(policyData.global || DEFAULT_NOTIFICATION_POLICY_GLOBAL);
    setBildirimPolitikalari(policyData.policies || DEFAULT_NOTIFICATION_POLICIES);
    setBildirimOtomasyonOzet(ozet || { aktifZamanli: 0, bekleyen: 0, bugunGonderilen: 0 });
    if (panelAc) setBildirimOtomasyonPanelAcik(true);
  } catch (err) {
    console.error("Bildirim otomasyonu yüklenemedi:", err);
    setBildirimOtomasyonDurum(t("mgmt.auto.loadFailed"));
  } finally {
    setBildirimOtomasyonYukleniyor(false);
  }
};

const bildirimOtomasyonPanelToggle = () => {
  if (bildirimOtomasyonPanelAcik) {
    setBildirimOtomasyonPanelAcik(false);
  } else {
    bildirimOtomasyonunuYukle();
  }
};

const bildirimGlobalPolicyGuncelle = (alan, deger) => {
  setBildirimPolitikaGlobal((prev) => ({ ...prev, [alan]: deger }));
};

const bildirimPolitikaLocalGuncelle = (policyKey, alan, deger) => {
  setBildirimPolitikalari((prev) =>
    prev.map((policy) => (policy.key === policyKey ? { ...policy, [alan]: deger } : policy))
  );
};

const bildirimPolitikaRuleGuncelle = (policyKey, ruleKey, alan, deger) => {
  setBildirimPolitikalari((prev) =>
    prev.map((policy) => {
      if (policy.key !== policyKey) return policy;
      return {
        ...policy,
        rules: (policy.rules || []).map((rule) =>
          rule.key === ruleKey ? { ...rule, [alan]: deger } : rule
        ),
      };
    })
  );
};

const bildirimPolitikaRuleEkle = (policyKey) => {
  const key = `custom_${Date.now()}`;
  setBildirimPolitikalari((prev) =>
    prev.map((policy) => {
      if (policy.key !== policyKey) return policy;
      return {
        ...policy,
        rules: [
          ...(policy.rules || []),
          { key, label: t("mgmt.finance.customRule"), enabled: true, daysBefore: 1, time: "09:00", custom: true, ...(policyKey === "financeReminder" ? { financeKind: "bill" } : {}) },
        ],
      };
    })
  );
};

const bildirimPolitikaRuleSil = (policyKey, ruleKey) => {
  setBildirimPolitikalari((prev) =>
    prev.map((policy) => {
      if (policy.key !== policyKey) return policy;
      return {
        ...policy,
        rules: (policy.rules || []).filter((rule) => rule.key !== ruleKey),
      };
    })
  );
};

const bildirimGlobalPolicyKaydet = async () => {
  try {
    setBildirimOtomasyonKaydediliyor("global");
    await bildirimPolitikasiKaydet("global", bildirimPolitikaGlobal);
    setBildirimOtomasyonDurum(t("mgmt.auto.quietSavedOk"));
  } catch (err) {
    console.error("Genel bildirim politikası kaydedilemedi:", err);
    setBildirimOtomasyonDurum(t("mgmt.auto.policyFailed"));
  } finally {
    setBildirimOtomasyonKaydediliyor("");
  }
};

const bildirimPolitikaKaydetClick = async (policy) => {
  try {
    setBildirimOtomasyonKaydediliyor(policy.key);
    await bildirimPolitikasiKaydet(policy.key, policy);
    setBildirimOtomasyonDurum(`${policy.title} kaydedildi ✓`);
    await bildirimOtomasyonunuYukle({ panelAc: false });
  } catch (err) {
    console.error("Bildirim politikası kaydedilemedi:", err);
    setBildirimOtomasyonDurum(t("mgmt.auto.policyFailed"));
  } finally {
    setBildirimOtomasyonKaydediliyor("");
  }
};

const bildirimPolitikalariVarsayilanaDonClick = async () => {
  const onay = await appConfirm(t("mgmt.auto.confirmReset"));
  if (!onay) return;

  try {
    setBildirimOtomasyonKaydediliyor("defaults");
    await bildirimPolitikalariVarsayilanaKur();
    setBildirimOtomasyonDurum(t("mgmt.auto.defaultsOk"));
    await bildirimOtomasyonunuYukle({ panelAc: false });
  } catch (err) {
    console.error("Varsayılan politikalar kurulamadı:", err);
    setBildirimOtomasyonDurum(t("mgmt.auto.defaultsFailed"));
  } finally {
    setBildirimOtomasyonKaydediliyor("");
  }
};

const testBildirimiGonder = async () => {
  try {
    if (!profil?.familyId || !profil?.uid) {
      appAlert(t("mgmt.notif.userFamilyNotFound"));
      return;
    }

    setTestBildirimGonderiliyor(true);
    setTestBildirimDurum("");

    await bildirimEkleService({
      familyId: profil.familyId,
      aliciUid: profil.uid,
      baslik: t("mgmt.notif.testTitle"),
      aciklama: t("mgmt.notif.testDesc"),
      baslikKey: "mgmt.notif.testTitle",
      aciklamaKey: "mgmt.notif.testDesc",
      type: "system",
      kaynak: "notificationAdmin",
      kaynakId: "test",
      gonderenUid: profil.uid,
      idempotencyKey: `test_notification_${Date.now()}`,
    });

    setTestBildirimDurum(t("mgmt.notif.testOk"));
  } catch (err) {
    console.error("Test bildirimi gönderilemedi:", err);
    setTestBildirimDurum(t("mgmt.notif.testFailed"));
  } finally {
    setTestBildirimGonderiliyor(false);
  }
};

const globalKullanicilariYenile = async () => {
  if (!bildirimYonetebilir) return [];

  setGlobalKullanicilarYukleniyor(true);
  try {
    const liste = await tumUygulamaKullanicilariniGetir();
    setGlobalKullanicilar(liste);
    setGlobalKullaniciSonYenileme(new Date());

    // Silinen/pasifleşen kullanıcı seçili kaldıysa canlı listeye göre temizle.
    const aktifUidSet = new Set(liste.map((u) => u.uid).filter(Boolean));
    setSeciliUidler((prev) => prev.filter((uid) => aktifUidSet.has(uid)));
    return liste;
  } catch (err) {
    console.error("Uygulama kullanıcıları yüklenemedi:", err);
    return [];
  } finally {
    setGlobalKullanicilarYukleniyor(false);
  }
};

const kullaniciSurumleri = useMemo(
  () => adminUserVersionOptions(globalKullanicilar),
  [globalKullanicilar]
);
const filtreliGlobalKullanicilar = useMemo(() => filterAndSortAdminUsers(globalKullanicilar, {
  query: kullaniciArama,
  sort: kullaniciSiralamasi,
  platform: kullaniciPlatformFiltresi,
  delivery: kullaniciKanalFiltresi,
  language: kullaniciDilFiltresi,
  version: kullaniciSurumFiltresi,
  activity: kullaniciAktiviteFiltresi,
  premium: kullaniciPremiumFiltresi,
}), [
  globalKullanicilar,
  kullaniciArama,
  kullaniciSiralamasi,
  kullaniciPlatformFiltresi,
  kullaniciKanalFiltresi,
  kullaniciDilFiltresi,
  kullaniciSurumFiltresi,
  kullaniciAktiviteFiltresi,
  kullaniciPremiumFiltresi,
]);
const aktifKullaniciFiltreSayisi = [
  kullaniciPlatformFiltresi,
  kullaniciKanalFiltresi,
  kullaniciDilFiltresi,
  kullaniciSurumFiltresi,
  kullaniciAktiviteFiltresi,
  kullaniciPremiumFiltresi,
].filter((value) => value !== "all").length + (kullaniciSiralamasi !== "newest" ? 1 : 0);
const kullaniciFiltreleriniTemizle = () => {
  setKullaniciSiralamasi("newest");
  setKullaniciPlatformFiltresi("all");
  setKullaniciKanalFiltresi("all");
  setKullaniciDilFiltresi("all");
  setKullaniciSurumFiltresi("all");
  setKullaniciAktiviteFiltresi("all");
  setKullaniciPremiumFiltresi("all");
};

const kullaniciPlatformu = (u) => {
  const value = String(u?.platform || u?.devicePlatform || "unknown").toLowerCase();
  if (value.includes("ios") || value.includes("iphone") || value.includes("ipad")) return "ios";
  if (value.includes("android")) return "android";
  return "other";
};
const kullaniciPlatformdaMi = (u, platform) => {
  const platforms = Array.isArray(u?.platforms) ? u.platforms : [u?.platform];
  return platforms.some((value) => kullaniciPlatformu({ platform:value }) === platform);
};
const sistemDuyuruHedefleriFiltrele = (liste = []) => liste.filter((u) => {
  const locale = String(u?.locale || "tr").toLowerCase().startsWith("en") ? "en" : "tr";
  return (sistemDuyuruDilFiltresi === "all" || locale === sistemDuyuruDilFiltresi)
    && (sistemDuyuruPlatformFiltresi === "all" || kullaniciPlatformdaMi(u, sistemDuyuruPlatformFiltresi));
});
const sistemDuyuruHedefleri = sistemDuyuruHedefleriFiltrele(globalKullanicilar);

const tumunuSeciliMi =
  filtreliGlobalKullanicilar.length > 0 &&
  filtreliGlobalKullanicilar.every((u) => seciliUidler.includes(u.uid));

const kullaniciSecToggle = (uid) => {
  setSeciliUidler((prev) =>
    prev.includes(uid)
      ? prev.filter((x) => x !== uid)
      : [...prev, uid]
  );
};

const tumunuSecToggle = () => {
  if (tumunuSeciliMi) {
    const gorunenUidler = filtreliGlobalKullanicilar.map((u) => u.uid);
    setSeciliUidler((prev) => prev.filter((uid) => !gorunenUidler.includes(uid)));
  } else {
    setSeciliUidler((prev) => {
      const set = new Set(prev);
      filtreliGlobalKullanicilar.forEach((u) => set.add(u.uid));
      return Array.from(set);
    });
  }
};

const hazirMesajSec = (mesaj) => {
  setKullaniciBaslik(`${mesaj.icon} ${mesaj.baslik}`);
  setKullaniciMesaj(mesaj.aciklama);
};

const sistemDuyuruKartiSec = (mesaj) => {
  setSistemDuyuruBaslik(`${mesaj.icon} ${mesaj.baslik}`);
  setSistemDuyuruMesaj(mesaj.aciklama);
  setSistemDuyuruBaslikEn(""); setSistemDuyuruMesajEn("");
  // Şablonun key referansını sakla — gönderim sırasında render edilmiş metin
  // yerine bu key kullanılır, alıcı kendi dilinde görür.
  setSistemDuyuruSecilenSablon(mesaj.baslikKey && mesaj.aciklamaKey
    ? { baslikKey: mesaj.baslikKey, aciklamaKey: mesaj.aciklamaKey, icon: mesaj.icon }
    : null);
};

// Admin başlığı/mesajı elle değiştirirse (şablon seçiliyken bile) artık o metin
// şablonla bire bir eşleşmeyebilir — key'i geçersiz kılıp serbest-metin moduna
// (TR/EN ikili giriş) geçiyoruz, böylece yanlış çeviri gönderilmesi engellenir.
const handleSistemDuyuruBaslikChange = (value) => {
  setSistemDuyuruBaslik(value);
  setSistemDuyuruSecilenSablon(null);
};
const handleSistemDuyuruMesajChange = (value) => {
  setSistemDuyuruMesaj(value);
  setSistemDuyuruSecilenSablon(null);
};

const sistemDuyuruPanelToggle = () => {
  setSistemDuyuruPanelAcik((prev) => {
    const next = !prev;
    if (next) globalKullanicilariYenile();
    return next;
  });
  setSistemDuyuruDurum("");
};

const sistemDuyurusuGonderClick = async () => {
  try {
    if (!superAdminMi || !profil?.uid) {
      appAlert(t("mgmt.sys.superAdminOnly"));
      return;
    }

    if (!sistemDuyuruBaslik.trim() || !sistemDuyuruMesaj.trim()) {
      appAlert(t("mgmt.notif.titleRequired"));
      return;
    }

    if (sistemDuyuruModu === "scheduled" && !sistemDuyuruPlanlananTarih) {
      appAlert(t("mgmt.notif.dateRequired"));
      return;
    }

    setSistemDuyuruGonderiliyor(true);
    setSistemDuyuruDurum("");

    const canlıKullanicilar = await globalKullanicilariYenile();
    const duyuruHedefleri = sistemDuyuruHedefleriFiltrele(canlıKullanicilar.length ? canlıKullanicilar : globalKullanicilar);
    if (!duyuruHedefleri.length) throw new Error(tr ? "Seçilen filtrelerde hedef kullanıcı yok." : "No users match the selected filters.");

    // Dil bilgisini taşıyacak ek alanlar:
    // 1) Hazır şablon seçiliyse -> baslikKey/aciklamaKey (backend kendi sözlüğünden
    //    her kullanıcının cihaz diline göre çevirir).
    // 2) Şablon seçili değilse ama admin hem TR hem EN alanını doldurduysa ->
    //    baslikI18n/aciklamaI18n (backend bu sözlükten cihaz diline göre seçer).
    // 3) İkisi de yoksa -> eski davranış: tek dilde (admin'in yazdığı dilde) gider.
    const ceviriAlanlari = sistemDuyuruSecilenSablon
      ? { baslikKey: sistemDuyuruSecilenSablon.baslikKey, aciklamaKey: sistemDuyuruSecilenSablon.aciklamaKey }
      : (sistemDuyuruBaslikEn.trim() && sistemDuyuruMesajEn.trim())
        ? {
            baslikI18n: { tr: sistemDuyuruBaslik.trim(), en: sistemDuyuruBaslikEn.trim() },
            aciklamaI18n: { tr: sistemDuyuruMesaj.trim(), en: sistemDuyuruMesajEn.trim() },
          }
        : {};

    if (sistemDuyuruModu === "scheduled") {
      await zamanliBildirimOlustur({
        hedefTipi: "allUsers",
        hedefUidler: duyuruHedefleri.map((u) => u.uid),
        hedefSayisi: duyuruHedefleri.length,
        baslik: sistemDuyuruBaslik.trim(),
        aciklama: sistemDuyuruMesaj.trim(),
        ...ceviriAlanlari,
        planlananTarih: sistemDuyuruPlanlananTarih,
        tekrarTipi: sistemDuyuruTekrarTipi,
        tip: "systemAnnouncementScheduled",
        bildirimTipi: "systemAnnouncement",
        kaynak: "notificationAdmin",
        olusturanUid: profil.uid,
      });

      setSistemDuyuruDurum(t("mgmt.sys.scheduledCreated", { count: duyuruHedefleri.length }));
      setGecmisAcik(false);
    } else {
      const sonuc = await tumKullanicilaraSistemDuyurusuGonder({
        kullanicilar: duyuruHedefleri,
        baslik: sistemDuyuruBaslik.trim(),
        aciklama: sistemDuyuruMesaj.trim(),
        ...ceviriAlanlari,
        gonderenUid: profil.uid,
      });

      if (sonuc.pushSent > 0) {
        setSistemDuyuruDurum(t("mgmt.sys.deliveryResult", {
          sent: sonuc.pushSent,
          inApp: sonuc.inAppCreated,
          failed: sonuc.pushFailed,
        }));
      } else {
        setSistemDuyuruDurum(t("mgmt.sys.pushDeliveryFailed", { count: sonuc.inAppCreated }));
      }
    }
  } catch (err) {
    console.error("Sistem duyurusu gönderilemedi:", err);
    setSistemDuyuruDurum(err.message || t("mgmt.sys.sendFailed"));
  } finally {
    setSistemDuyuruGonderiliyor(false);
  }
};

const kullanicilaraBildirimGonder = async () => {
  try {
    if (!profil?.uid) {
      appAlert(t("mgmt.notif.adminNotFound"));
      return;
    }

    if (seciliUidler.length === 0) {
      appAlert(t("mgmt.notif.minOneUser"));
      return;
    }

    if (!kullaniciBaslik.trim() || !kullaniciMesaj.trim()) {
      appAlert(t("mgmt.notif.titleRequired"));
      return;
    }

    setKullaniciBildirimGonderiliyor(true);
    setKullaniciBildirimDurum("");

    const canlıKullanicilar = await globalKullanicilariYenile();
    const kaynakListe = canlıKullanicilar.length ? canlıKullanicilar : globalKullanicilar;
    const hedefler = kaynakListe.filter((u) =>
      seciliUidler.includes(u.uid)
    );

    if (hedefler.length === 0) {
      appAlert(t("mgmt.notif.minOneUser"));
      return;
    }

    const adet = await seciliKullanicilaraBildirimGonder({
      kullanicilar: hedefler,
      baslik: kullaniciBaslik.trim(),
      aciklama: kullaniciMesaj.trim(),
      gonderenUid: profil.uid,
    });

    setKullaniciBildirimDurum(t("mgmt.user.sentToUsers", { count: adet }));
  } catch (err) {
    console.error("Kullanıcılara bildirim gönderilemedi:", err);
    setKullaniciBildirimDurum(t("mgmt.notif.sendFailed"));
  } finally {
    setKullaniciBildirimGonderiliyor(false);
  }
};
const zamanliBildirimKaydet = async () => {
  try {
    if (!profil?.uid) {
      appAlert(t("mgmt.notif.adminNotFound"));
      return;
    }

    if (seciliUidler.length === 0) {
      appAlert(t("mgmt.notif.minOneUser"));
      return;
    }

    if (!kullaniciBaslik.trim() || !kullaniciMesaj.trim()) {
      appAlert(t("mgmt.notif.titleRequired"));
      return;
    }

    if (!planlananTarih) {
      appAlert(t("mgmt.notif.dateRequired"));
      return;
    }

    const canlıKullanicilar = await globalKullanicilariYenile();
    const aktifUidSet = new Set((canlıKullanicilar.length ? canlıKullanicilar : globalKullanicilar).map((u) => u.uid));
    const aktifSeciliUidler = seciliUidler.filter((uid) => aktifUidSet.has(uid));
    if (!aktifSeciliUidler.length) {
      appAlert(t("mgmt.notif.minOneUser"));
      return;
    }

    await zamanliBildirimOlustur({
      hedefTipi: "users",

      hedefUidler: aktifSeciliUidler,

      baslik: kullaniciBaslik.trim(),

      aciklama: kullaniciMesaj.trim(),

      planlananTarih,

      tekrarTipi,

      tip: "scheduledAdmin",

      bildirimType: "scheduledAdmin",

      kaynak: "notificationAdmin",

      olusturanUid: profil.uid,

      extraData: { globalNotification: true, suppressFamilyName: true },
    });

    setKullaniciBildirimDurum(
      t("mgmt.sched.createdOk")
    );
  } catch (err) {
    console.error("Zamanlı bildirim oluşturulamadı:", err);

    setKullaniciBildirimDurum(
      t("mgmt.sched.createFailed")
    );
  }
};

const bildirimGecmisiYukle = async () => {
  try {
    setGecmisYukleniyor(true);

    const liste = await bildirimGecmisiniGetir(null, 100);

    setBildirimGecmisi(liste);
    setGecmisAcik(true);
  } catch (err) {
    console.error("Bildirim geçmişi yüklenemedi:", err);
    appAlert(t("mgmt.notif.historyFailed"));
  } finally {
    setGecmisYukleniyor(false);
  }
};

const bildirimAlicilariniToggle = async (item) => {
  if (!item?.id || Number(item.sentCount || 0) <= 0) return;
  if (acikBildirimAliciId === item.id) {
    setAcikBildirimAliciId(null);
    return;
  }
  setAcikBildirimAliciId(item.id);
  if (bildirimAlicilari[item.id]) return;
  try {
    setBildirimAlicilariYukleniyor(item.id);
    const result = await bildirimAlicilariniGetir(item.id);
    setBildirimAlicilari((current) => ({ ...current, [item.id]: result }));
  } catch {
    setBildirimAlicilari((current) => ({ ...current, [item.id]: { items:[], total:0 } }));
  } finally {
    setBildirimAlicilariYukleniyor(null);
  }
};

const dahaFazlaBildirimAlicisiYukle = async (item) => {
  const current = bildirimAlicilari[item.id] || { items:[], total:0 };
  if (bildirimAlicilariYukleniyor || current.items.length >= current.total) return;
  try {
    setBildirimAlicilariYukleniyor(item.id);
    const result = await bildirimAlicilariniGetir(item.id, current.items.length, 100);
    setBildirimAlicilari((all) => ({
      ...all,
      [item.id]: { items:[...current.items, ...result.items], total:result.total },
    }));
  } finally {
    setBildirimAlicilariYukleniyor(null);
  }
};

const siraliUyeler = sortFamilyMembersForManagement(uyeler || []);

const aileUyeleri = siraliUyeler
  .map((u) => ({ ...u, uid: u.uid || u.id }))
  .filter((u) => !!u.uid);

const filtreliAileUyeleri = aileUyeleri.filter((u) => {
  const q = aileBildirimArama.toLowerCase().trim();
  if (!q) return true;

  return (
    `${u.ad || ""} ${u.soyad || ""}`.toLowerCase().includes(q) ||
    `${u.rol || ""}`.toLowerCase().includes(q) ||
    `${u.kullaniciAdi || ""}`.toLowerCase().includes(q) ||
    `${u.email || ""}`.toLowerCase().includes(q)
  );
});

const aileTumunuSeciliMi =
  filtreliAileUyeleri.length > 0 &&
  filtreliAileUyeleri.every((u) => aileSeciliUidler.includes(u.uid));

const aileUyeSecToggle = (uid) => {
  setAileSeciliUidler((prev) =>
    prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
  );
};

const aileTumunuSecToggle = () => {
  const gorunenUidler = filtreliAileUyeleri.map((u) => u.uid);

  if (aileTumunuSeciliMi) {
    setAileSeciliUidler((prev) =>
      prev.filter((uid) => !gorunenUidler.includes(uid))
    );
    return;
  }

  setAileSeciliUidler((prev) => {
    const set = new Set(prev);
    gorunenUidler.forEach((uid) => set.add(uid));
    return Array.from(set);
  });
};

const aileHazirMesajSec = (mesaj) => {
  setAileBildirimBaslik(`${mesaj.icon} ${mesaj.baslik}`);
  setAileBildirimMesaj(mesaj.aciklama);
  setAileGonderimTuru(mesaj.gonderimTuru || "normal");
};

const sonAileBildirimleriniYukle = async () => {
  if (!profil?.familyId) return;

  try {
    const liste = await sonAileBildirimleriniGetir(profil.familyId, 5);
    setSonAileBildirimleri(liste);
  } catch (err) {
    console.error("Son aile bildirimleri yüklenemedi:", err);
  }
};

const zamanliAileBildirimleriniYukle = async ({ panelAc = true } = {}) => {
  if (!profil?.familyId) return;

  try {
    const liste = await aileZamanliBildirimleriniGetir(profil.familyId);
    setZamanliAileBildirimleri(liste);
    if (panelAc) setZamanliAilePanelAcik(true);
  } catch (err) {
    console.error("Zamanlı aile bildirimleri yüklenemedi:", err);
    appAlert(t("mgmt.sched.loadFailed"));
  }
};

const aileBildirimPaneliniAc = () => {
  const yeniDurum = !aileBildirimPanelAcik;
  setAileBildirimPanelAcik(yeniDurum);

  if (!aileBildirimBaslik) {
    setAileBildirimBaslik(t("mgmt.notif.familyAnnouncementTitle"));
    setAileBildirimMesaj(t("mgmt.notif.familyAnnouncementBody"));
  }

  if (yeniDurum) {
    sonAileBildirimleriniYukle();
    zamanliAileBildirimleriniYukle({ panelAc: false });
  }
};

const aileBildirimPaneliniKapat = () => {
  setAileBildirimPanelAcik(false);
  setAileBildirimDurum("");
};

const aileBildirimiTekrarla = (item) => {
  setAileBildirimPanelAcik(true);
  setAileBildirimBaslik(item.baslik || "");
  setAileBildirimMesaj(item.aciklama || "");
  setAileGonderimTuru(item.gonderimTuru || "normal");
  setAileSeciliUidler(Array.isArray(item.hedefUidler) ? item.hedefUidler : []);
  setAileBildirimModu("now");
  setAileBildirimDurum(t("mgmt.notif.copiedToForm"));
};

const aileBildirimiGonderClick = async () => {
  try {
    if (!profil?.familyId || !profil?.uid) {
      appAlert(t("mgmt.notif.adminNotFound"));
      return;
    }

    if (aileSeciliUidler.length === 0) {
      appAlert(t("mgmt.notif.minOneUser"));
      return;
    }

    if (!aileBildirimBaslik.trim() || !aileBildirimMesaj.trim()) {
      appAlert(t("mgmt.notif.titleRequired"));
      return;
    }

    if (aileBildirimModu === "scheduled" && !ailePlanlananTarih) {
      appAlert(t("mgmt.notif.dateRequired"));
      return;
    }

    setAileBildirimGonderiliyor(true);
    setAileBildirimDurum("");

    if (aileBildirimModu === "scheduled") {
      await aileZamanliBildirimiOlustur({
        familyId: profil.familyId,
        hedefUidler: aileSeciliUidler,
        baslik: aileBildirimBaslik.trim(),
        aciklama: aileBildirimMesaj.trim(),
        planlananTarih: ailePlanlananTarih,
        tekrarTipi: aileTekrarTipi,
        gonderimTuru: aileGonderimTuru,
        olusturanUid: profil.uid,
      });

      setAileBildirimDurum(t("mgmt.notif.scheduledCreated"));
      await zamanliAileBildirimleriniYukle();
    } else {
      const sonuc = await aileBildirimiGonder({
        familyId: profil.familyId,
        uyeler: aileUyeleri,
        hedefUidler: aileSeciliUidler,
        baslik: aileBildirimBaslik.trim(),
        aciklama: aileBildirimMesaj.trim(),
        gonderenUid: profil.uid,
        gonderimTuru: aileGonderimTuru,
      });

      setAileBildirimDurum(t("mgmt.family.sentToMembers", { count: sonuc.adet }));
      if (sonuc.adet > 0) {
        setAileSeciliUidler([]);
      }
      await sonAileBildirimleriniYukle();
    }
  } catch (err) {
    console.error("Aile bildirimi gönderilemedi:", err);
    setAileBildirimDurum(err.message || t("mgmt.notif.sendFailed"));
  } finally {
    setAileBildirimGonderiliyor(false);
  }
};

const aileOkunmaDurumuYukle = async (item) => {
  try {
    const durum = await aileBildirimiOkunmaDurumuGetir({
      familyId: profil.familyId,
      bildirimId: item.id,
      hedefUidler: item.hedefUidler || [],
    });

    setOkunmaDetaylari((prev) => ({ ...prev, [item.id]: durum }));
  } catch (err) {
    console.error("Okunma durumu yüklenemedi:", err);
  }
};

const zamanliAileBildirimiGuncelleClick = async (item, alan, deger) => {
  try {
    await aileZamanliBildirimiGuncelle(item.id, { [alan]: deger });
    await zamanliAileBildirimleriniYukle();
  } catch (err) {
    console.error("Zamanlı bildirim güncellenemedi:", err);
    appAlert(t("mgmt.notif.updateFailed"));
  }
};

const zamanliAileBildirimiIptalClick = async (item) => {
  const onay = await appConfirm(t("mgmt.confirm.cancelScheduled"));
  if (!onay) return;

  try {
    await aileZamanliBildirimiIptalEt(item.id);
    await zamanliAileBildirimleriniYukle();
  } catch (err) {
    console.error("Zamanlı bildirim iptal edilemedi:", err);
    appAlert(t("mgmt.notif.cancelFailed"));
  }
};

useEffect(() => {
  if (!aileBildirimYonetebilir || !profil?.familyId) return;
  sonAileBildirimleriniYukle();
  zamanliAileBildirimleriniYukle({ panelAc: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [aileBildirimYonetebilir, profil?.familyId]);

  return (
    <div className="pageContent userManagementPage">
      <div className="darkCard" style={{ marginBottom: "12px" }}>
        <p className="uyeYonetimAciklama">
          {t("mgmt.description")}
        </p>
      </div>

      <div className="tableCard">
        {uyeler.length === 0 && (
          <div className="emptyState">{t("mgmt.noMembers")}</div>
        )}
        {siraliUyeler.map((uye, idx) => (
          <div
            key={uye.id}
            className={`tableRow ${idx === siraliUyeler.length - 1 ? "tableRowLast" : ""}`}
          >
            <div className="memberAvatar sm">
              <AvatarImg
                avatar={uye.avatar || null}
                renk={uye.calendarColor || "#6366f1"}
                initials={`${uye.ad?.[0]||""}${uye.soyad?.[0]||""}`}
                size={32}
                glow={false}
                radius="50%"
              
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
            </div>
            <div className="tableRowContent">
              <span className="tableRowTitle">{uye.ad} {uye.soyad}</span>
              <span className="tableRowSub">
                {memberRoleLine(lang, uye, t)}
              </span>
            </div>
            {uyeYonetebilir && (
              <button
                className="editPermBtn"
                onClick={() => setSeciliYetkiUye(uye)}
              >
                {t("mgmt.permission")}
              </button>
            )}
          </div>
        ))}
      </div>



      {aileBildirimYonetebilir && (
        <div className="darkCard familyNotifyPanel">
          <div className="familyNotifyHeader">
            <div>
              <div className="familyNotifyTitle">
                <img src={imgAilem} alt="" aria-hidden="true" />
                <span>{t("mgmt.familyNotify.title")}</span>
              </div>
              <p className="familyNotifyText">
                {t("mgmt.familyNotify.desc")}
              </p>
            </div>
          </div>

          <div className="familyNotifyTopActions">
            <button className="primaryButton" onClick={aileBildirimPaneliniAc}>
              {aileBildirimPanelAcik ? t("mgmt.notif.closeBtn") : t("mgmt.notif.sendBtn")}
            </button>

            <button
  className="darkSecondaryBtn"
  onClick={() => {
    if (zamanliAilePanelAcik) {
      setZamanliAilePanelAcik(false);
    } else {
      zamanliAileBildirimleriniYukle();
    }
  }}
>
  {zamanliAilePanelAcik
    ? t("mgmt.notif.scheduledClose")
    : t("mgmt.notif.scheduledOpen")}
</button>
          </div>

          {aileBildirimPanelAcik && (
            <div className="familyNotifyBody">
              <div className="familySectionHeader">
                <strong>{t("mgmt.notify.prepare")}</strong>
                <button className="ayarKapatBtn" type="button" onClick={aileBildirimPaneliniKapat}>×</button>
              </div>
              <div className="inputLabelGroup">
                <label className="darkLabel">{t("mgmt.searchUser")}</label>
                <input
                  className="darkInput"
                  value={aileBildirimArama}
                  onChange={(e) => setAileBildirimArama(e.target.value)}
                  placeholder={t("mgmt.notif.searchPlaceholder")}
                />
              </div>

              <div className="familyNotifySelectBox">
                <label className="toggleLine">
                  <span className="toggleLabelText">{t("mgmt.selectAllVisibleFamily")}</span>
                  <div
                    className={`toggleSwitch ${aileTumunuSeciliMi ? "on" : ""}`}
                    onClick={aileTumunuSecToggle}
                  >
                    <div className="toggleThumb" />
                  </div>
                </label>
                <p className="ayarAciklama" style={{ marginTop: "8px" }}>
                  {t("mgmt.selectedFamilyCount", { selected: aileSeciliUidler.length, total: aileUyeleri.length })}
                </p>
              </div>

              <div className="familyMemberPicker">
                {filtreliAileUyeleri.length === 0 && (
                  <div className="emptyState">{t("mgmt.noFamilySearch")}</div>
                )}
                {filtreliAileUyeleri.map((u) => {
                  const secili = aileSeciliUidler.includes(u.uid);

                  return (
                    <button
                      key={u.uid}
                      type="button"
                      className={`familyMemberPickBtn ${secili ? "active" : ""}`}
                      onClick={() => aileUyeSecToggle(u.uid)}
                    >
                      <span className="memberAvatar sm familyPickAvatar">
                        <AvatarImg
                          avatar={u.avatar || u.photoURL || null}
                          renk={u.calendarColor || u.color || "#6366f1"}
                          initials={`${u.ad?.[0]||""}${u.soyad?.[0]||""}`}
                          size={32}
                          glow={false}
                          radius="50%"
                        
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                      </span>
                      <span>
                        <strong>{secili ? "✅ " : ""}{u.ad || "-"} {u.soyad || ""}</strong>
                        <small>{memberRoleLine(lang, u, t)}</small>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="inputLabelGroup">
                <label className="darkLabel">{t("mgmt.familyTemplates")}</label>
                <div className="familyTemplateGrid">
                  {getHazirAileBildirimMesajlari(t).map((mesaj) => (
                    <button
                      key={mesaj.id}
                      type="button"
                      className="familyTemplateCard"
                      onClick={() => aileHazirMesajSec(mesaj)}
                    >
                      <span className="familyTemplateIcon">{mesaj.icon}</span>
                      <strong>{mesaj.baslik}</strong>
                      <small>{mesaj.aciklama}</small>
                    </button>
                  ))}
                </div>
              </div>

              <div className="familyNotifyFormGrid">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.title")}</label>
                  <input
                    className="darkInput"
                    value={aileBildirimBaslik}
                    onChange={(e) => setAileBildirimBaslik(e.target.value)}
                    placeholder={t("mgmt.notif.titlePlaceholder")}
                  />
                </div>

                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.sendType")}</label>
                  <select
                    className="darkInput"
                    value={aileGonderimTuru}
                    onChange={(e) => setAileGonderimTuru(e.target.value)}
                  >
                    {getAileGonderimTurleri(t).map((tur) => (
                      <option key={tur.value} value={tur.value}>{tur.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="inputLabelGroup">
                <label className="darkLabel">{t("mgmt.field.message")}</label>
                <textarea
                  className="darkInput"
                  value={aileBildirimMesaj}
                  onChange={(e) => setAileBildirimMesaj(e.target.value)}
                  placeholder={t("mgmt.notif.bodyPlaceholder")}
                  rows={3}
                />
              </div>

              <div className="familySendModeBox">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.sendTime")}</label>
                  <select
                    className="darkInput"
                    value={aileBildirimModu}
                    onChange={(e) => setAileBildirimModu(e.target.value)}
                  >
                    <option value="now">{t("mgmt.send.now")}</option>
                    <option value="scheduled">{t("mgmt.send.scheduled")}</option>
                  </select>
                </div>

                {aileBildirimModu === "scheduled" && (
                  <div className="familyNotifyFormGrid">
                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("mgmt.field.dateTime")}</label>
                      <input
                        type="datetime-local"
                        className="darkInput"
                        value={ailePlanlananTarih}
                        onChange={(e) => setAilePlanlananTarih(e.target.value)}
                      />
                    </div>

                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("mgmt.field.repeatType")}</label>
                      <select
                        className="darkInput"
                        value={aileTekrarTipi}
                        onChange={(e) => setAileTekrarTipi(e.target.value)}
                      >
                        {getAileTekrarTipleri(t).map((tekrar) => (
                          <option key={tekrar.value} value={tekrar.value}>{tekrar.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="primaryButton"
                onClick={aileBildirimiGonderClick}
                disabled={aileBildirimGonderiliyor}
              >
                {aileBildirimGonderiliyor
                  ? t("mgmt.notif.sending")
                  : aileBildirimModu === "scheduled"
                    ? t("mgmt.family.scheduleSelected", { count: aileSeciliUidler.length })
                    : t("mgmt.family.sendSelected", { count: aileSeciliUidler.length })}
              </button>

              {aileBildirimDurum && (
                <div
                  className={aileBildirimDurum.includes("✓") ? "profilSuccess" : "profilError"}
                  style={{ marginTop: "10px" }}
                >
                  {aileBildirimDurum}
                </div>
              )}

              <div className="familyRecentBox">
                <button
                  type="button"
                  className="familyRecentToggleBtn"
                  onClick={() => {
                    const next = !sonAileBildirimPanelAcik;
                    setSonAileBildirimPanelAcik(next);
                    if (next) sonAileBildirimleriniYukle();
                  }}
                >
                  <span>{t("mgmt.notify.recent")}</span>
                  <span>{sonAileBildirimPanelAcik ? "▴" : "▾"}</span>
                </button>

                {sonAileBildirimPanelAcik && (
                  <>
                    <div className="familySectionHeader familyRecentSubHeader">
                      <strong>{t("mgmt.notify.recent")}</strong>
                      <button className="darkSecondaryBtn" onClick={sonAileBildirimleriniYukle}>{mLabel(lang, "Yenile")}</button>
                    </div>

                    {sonAileBildirimleri.length === 0 ? (
                      <div className="emptyState">{t("mgmt.noFamilyNotif")}</div>
                    ) : (
                      <div className="familyRecentList familyRecentListCompact">
                    {sonAileBildirimleri.map((item) => {
                      const okuma = okunmaDetaylari[item.id];
                      const hedefSayisi = item.hedefSayisi || item.hedefUidler?.length || 0;

                      return (
                        <div key={item.id} className="familyRecentItem">
                          <div className="familyRecentContent">
                            <div className="familyRecentItemHead">
                              <strong>{item.baslik || t("mgmt.notif.untitled")}</strong>
                              {familyNotificationDate(item.createdAt, lang) && <time>{familyNotificationDate(item.createdAt, lang)}</time>}
                            </div>
                            <p>{item.aciklama || t("mgmt.noMessage")}</p>
                            <div className="familyRecentReadSummary">
                              <span>👁️ {mLabel(lang, "Okundu")}: {okuma ? `${okuma.okunanSayisi}/${okuma.hedefSayisi}` : `-/${hedefSayisi}`}</span>
                            </div>

                            {okuma && (
                              <div className="familyReadDetails">
                                <div className="familyReadColumn read">
                                  <span>{t("mgmt.notify.readers")}</span>
                                  {okuma.okuyanlar?.length > 0 ? (
                                    okuma.okuyanlar.map((uye) => (
                                      <small key={uye.uid}>✅ {uye.displayName} · {mLabel(lang, uye.rol || "", t("mgmt.role.familyMember"))}</small>
                                    ))
                                  ) : (
                                    <small>{t("mgmt.notify.noReaders")}</small>
                                  )}
                                </div>

                                <div className="familyReadColumn unread">
                                  <span>{t("mgmt.notify.unread")}</span>
                                  {okuma.okumayanlar?.length > 0 ? (
                                    okuma.okumayanlar.map((uye) => (
                                      <small key={uye.uid}>⏳ {uye.displayName} · {mLabel(lang, uye.rol || "", t("mgmt.role.familyMember"))}</small>
                                    ))
                                  ) : (
                                    <small>{t("mgmt.notify.allRead")}</small>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="familyRecentActions">
                            <button className="darkSecondaryBtn" onClick={() => aileBildirimiTekrarla(item)}>
                              🔁 {t("mgmt.resend")}
                            </button>
                            <button className="darkSecondaryBtn" onClick={() => aileOkunmaDurumuYukle(item)}>
                              {okuma ? mLabel(lang, "Okunma Yenile") : mLabel(lang, "Kim Okudu")}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {zamanliAilePanelAcik && (
            <div className="familyScheduledBox">
              <div className="familySectionHeader">
                <strong>{t("mgmt.notify.scheduled")}</strong>
                <button className="ayarKapatBtn" onClick={() => setZamanliAilePanelAcik(false)}>×</button>
              </div>

              {zamanliAileBildirimleri.length === 0 ? (
                <div className="emptyState">{t("mgmt.noScheduledFamily")}</div>
              ) : (
                <div className="familyRecentList familyRecentListScheduledCompact">
                  {zamanliAileBildirimleri.map((item) => (
                    <div key={item.id} className="familyRecentItem scheduled">
                      <div>
                        <strong>{item.baslik || t("mgmt.notif.untitled")}</strong>
                        <small>{item.aciklama || t("mgmt.noMessage")}</small>
                        <small>
                          Durum: {item.durum || "bekliyor"} · Tekrar: {getAileTekrarTipleri(t).find((tekrar) => tekrar.value === item.tekrarTipi)?.label || item.tekrarTipi || "Tek Sefer"}
                        </small>
                      </div>

                      <div className="familyScheduledEditGrid">
                        <input
                          type="datetime-local"
                          className="darkInput"
                          value={(item.planlananTarih || "").slice(0, 16)}
                          onChange={(e) => zamanliAileBildirimiGuncelleClick(item, "planlananTarih", e.target.value)}
                        />
                        <select
                          className="darkInput"
                          value={item.tekrarTipi || "none"}
                          onChange={(e) => zamanliAileBildirimiGuncelleClick(item, "tekrarTipi", e.target.value)}
                        >
                          {getAileTekrarTipleri(t).map((tekrar) => (
                            <option key={tekrar.value} value={tekrar.value}>{tekrar.label}</option>
                          ))}
                        </select>
                        <button className="darkSecondaryBtn danger" onClick={() => zamanliAileBildirimiIptalClick(item)}>
                          {t("mgmt.cancel")}
                        </button>
                      </div>
                    </div>
                  ))}
                  {dahaFazlaZamanlanmisVar && (
                    <button
                      type="button"
                      className="darkSecondaryBtn scheduledAdminMoreBtn"
                      onClick={() => setZamanlanmisGosterimAdedi((n) => n + 10)}
                    >
                      {t("common.showMore")} ({filtreliZamanlanmisBildirimler.length - gorunenZamanlanmisBildirimler.length})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}


          {bildirimYonetebilir && (
  <SuperAdminAccordion
    icon="🔔"
    title={t("mgmt.notifManagement")}
    description={bildirimYonetebilir ? t("mgmt.notif.sysAdminPanel") : t("mgmt.notif.familyPanel")}
    open={bildirimYonetimiPaneliAcik}
    onToggle={() => setBildirimYonetimiPaneliAcik((value) => !value)}
  >
    <div className="notificationManagementActions">
      {bildirimYonetebilir && (
        <>
          <button
            className="primaryButton"
            onClick={testBildirimiGonder}
            disabled={testBildirimGonderiliyor}
          >
            {testBildirimGonderiliyor
              ? t("mgmt.notif.testSending")
              : t("mgmt.notif.testBtn")}
          </button>

          {testBildirimDurum && (
            <div
              className={
                testBildirimDurum.includes("✓")
                  ? "profilSuccess"
                  : "profilError"
              }
              style={{ marginTop: "8px" }}
            >
              {testBildirimDurum}
            </div>
          )}

          <button
            className="darkSecondaryBtn"
            onClick={() => {
              const next = !kullaniciBildirimAcik;
              setKullaniciBildirimAcik(next);
              if (next) globalKullanicilariYenile();
            }}
          >
            👤 {t("mgmt.userNotify")}
          </button>
        </>
      )}

      {kullaniciBildirimAcik && (
        <div
          className="darkCard"
          style={{
            marginTop: "10px",
            padding: "14px",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          {bildirimYonetebilir && (
            <>
              <div className="inputLabelGroup" style={{ marginBottom: "10px" }}>
                <label className="darkLabel">{t("mgmt.searchUser")}</label>
                <input
                  className="darkInput"
                  value={kullaniciArama}
                  onChange={(e) => setKullaniciArama(e.target.value)}
                  placeholder={t("mgmt.notif.userSearch")}
                />
              </div>

              <div className="adminUserFilterShell">
                <div className="adminUserFilterToolbar">
                  <button
                    type="button"
                    className={`darkSecondaryBtn adminUserFilterToggle ${kullaniciFiltrePaneliAcik ? "active" : ""}`}
                    onClick={() => setKullaniciFiltrePaneliAcik((value) => !value)}
                    aria-expanded={kullaniciFiltrePaneliAcik}
                  >
                    <span aria-hidden="true">⚙️</span>
                    <span>{tr ? "Filtrele ve sırala" : "Filter and sort"}</span>
                    {aktifKullaniciFiltreSayisi > 0 && <b>{aktifKullaniciFiltreSayisi}</b>}
                  </button>
                  <button
                    type="button"
                    className="darkSecondaryBtn adminUserRefreshBtn"
                    onClick={globalKullanicilariYenile}
                    disabled={globalKullanicilarYukleniyor}
                    aria-label={tr ? "Kullanıcı listesini yenile" : "Refresh user list"}
                  >
                    <span aria-hidden="true">↻</span>
                    <span>{globalKullanicilarYukleniyor ? (tr ? "Yenileniyor" : "Refreshing") : (tr ? "Yenile" : "Refresh")}</span>
                  </button>
                </div>

                <div className="adminUserFilterSummary">
                  <span>
                    {tr ? "Gösterilen" : "Showing"}: <strong>{filtreliGlobalKullanicilar.length}</strong> / {globalKullanicilar.length}
                  </span>
                  {globalKullaniciSonYenileme && (
                    <time dateTime={globalKullaniciSonYenileme.toISOString()}>
                      {tr ? "Son güncelleme" : "Updated"}: {globalKullaniciSonYenileme.toLocaleTimeString(tr ? "tr-TR" : "en-US", { hour: "2-digit", minute: "2-digit" })}
                    </time>
                  )}
                </div>

                {kullaniciFiltrePaneliAcik && (
                  <div className="adminUserFilterPanel">
                    <label className="adminUserFilterField adminUserFilterFieldWide">
                      <span>{tr ? "Sıralama" : "Sort"}</span>
                      <select className="darkInput" value={kullaniciSiralamasi} onChange={(e) => setKullaniciSiralamasi(e.target.value)}>
                        <option value="newest">{tr ? "En yeni üyeler" : "Newest members"}</option>
                        <option value="oldest">{tr ? "En eski üyeler" : "Oldest members"}</option>
                        <option value="nameAsc">{tr ? "Ad A–Z" : "Name A–Z"}</option>
                        <option value="nameDesc">{tr ? "Ad Z–A" : "Name Z–A"}</option>
                        <option value="emailAsc">{tr ? "E-posta A–Z" : "Email A–Z"}</option>
                        <option value="emailDesc">{tr ? "E-posta Z–A" : "Email Z–A"}</option>
                        <option value="platform">{tr ? "Platforma göre" : "By platform"}</option>
                        <option value="push">{tr ? "Push hazır önce" : "Push ready first"}</option>
                        <option value="language">{tr ? "Dile göre" : "By language"}</option>
                        <option value="versionDesc">{tr ? "En güncel sürüm" : "Newest version"}</option>
                        <option value="versionAsc">{tr ? "En eski sürüm" : "Oldest version"}</option>
                        <option value="activeDesc">{tr ? "En son aktif" : "Most recently active"}</option>
                        <option value="activeAsc">{tr ? "En uzun süredir pasif" : "Least recently active"}</option>
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Platform" : "Platform"}</span>
                      <select className="darkInput" value={kullaniciPlatformFiltresi} onChange={(e) => setKullaniciPlatformFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        <option value="ios">iOS</option>
                        <option value="android">Android</option>
                        <option value="multi">{tr ? "Çoklu cihaz" : "Multiple devices"}</option>
                        <option value="web">Web</option>
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Bildirim kanalı" : "Notification channel"}</span>
                      <select className="darkInput" value={kullaniciKanalFiltresi} onChange={(e) => setKullaniciKanalFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        <option value="push">Push</option>
                        <option value="in_app">In-App</option>
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Dil" : "Language"}</span>
                      <select className="darkInput" value={kullaniciDilFiltresi} onChange={(e) => setKullaniciDilFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        <option value="tr">TR</option>
                        <option value="en">EN</option>
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Sürüm" : "Version"}</span>
                      <select className="darkInput" value={kullaniciSurumFiltresi} onChange={(e) => setKullaniciSurumFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        {kullaniciSurumleri.map((version) => <option key={version} value={version}>{version}</option>)}
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Son aktivite" : "Last activity"}</span>
                      <select className="darkInput" value={kullaniciAktiviteFiltresi} onChange={(e) => setKullaniciAktiviteFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        <option value="live">{tr ? "Şu an aktif" : "Active now"}</option>
                        <option value="24h">{tr ? "Son 24 saat" : "Last 24 hours"}</option>
                        <option value="7d">{tr ? "Son 7 gün" : "Last 7 days"}</option>
                        <option value="30d">{tr ? "Son 30 gün" : "Last 30 days"}</option>
                        <option value="inactive30d">{tr ? "30+ gündür pasif" : "Inactive for 30+ days"}</option>
                        <option value="never">{tr ? "Aktivite kaydı yok" : "No activity recorded"}</option>
                      </select>
                    </label>

                    <label className="adminUserFilterField">
                      <span>{tr ? "Üyelik planı" : "Membership plan"}</span>
                      <select className="darkInput" value={kullaniciPremiumFiltresi} onChange={(e) => setKullaniciPremiumFiltresi(e.target.value)}>
                        <option value="all">{tr ? "Tümü" : "All"}</option>
                        <option value="free">{tr ? "Ücretsiz" : "Free"}</option>
                        <option value="premium">Premium</option>
                      </select>
                    </label>

                    <button type="button" className="darkSecondaryBtn adminUserClearFilters" onClick={kullaniciFiltreleriniTemizle} disabled={aktifKullaniciFiltreSayisi === 0}>
                      {tr ? "Filtreleri temizle" : "Clear filters"}
                    </button>
                  </div>
                )}

                <p className="adminUserChannelHint">
                  <span className="isPush">PUSH</span> {tr ? "aktif cihazına push ulaşabilir" : "can receive push on an active device"}
                  <span aria-hidden="true"> · </span>
                  <span className="isInApp">IN-APP</span> {tr ? "yalnız uygulama içinde görür" : "is available only inside the app"}
                </p>
              </div>

              <div
                className="darkCard"
                style={{ padding: "10px", marginBottom: "10px" }}
              >
                <label className="toggleLine">
                  <span className="toggleLabelText">
                    {t("mgmt.selectAllVisibleUsers")}
                  </span>
                  <div
                    className={`toggleSwitch ${tumunuSeciliMi ? "on" : ""}`}
                    onClick={tumunuSecToggle}
                  >
                    <div className="toggleThumb" />
                  </div>
                </label>

                <p className="ayarAciklama" style={{ marginTop: "8px" }}>
                  {t("mgmt.selectedUserCount", { selected: seciliUidler.length, total: globalKullanicilar.length })}
                </p>
              </div>

              <div
                className="adminUserNotifyUserList adminScrollList5"
                style={{
                  display: "grid",
                  gap: "8px",
                  marginBottom: "12px",
                  paddingRight: "4px",
                }}
              >
                {filtreliGlobalKullanicilar.map((u) => {
                  const secili = seciliUidler.includes(u.uid);

                  return (
                    <button
                      key={u.uid}
                      type="button"
                      className={`darkSecondaryBtn adminUserNotifyUser ${secili ? "active" : ""}`}
                      style={{
                        textAlign: "left",
                        minHeight: "62px",
                        borderColor: secili
                          ? "rgba(99,102,241,0.7)"
                          : undefined,
                        background: secili
                          ? "rgba(99,102,241,0.16)"
                          : undefined,
                      }}
                      onClick={() => kullaniciSecToggle(u.uid)}
                    >
                      <strong className="adminUserNotifyUserName">
                        {secili ? "✅ " : ""}
                        {u.ad || "-"} {u.soyad || ""}
                      </strong>
                      <span className="adminUserNotifyUserMeta">
                        <span className="adminUserNotifyUserIdentity">
                          {u.email || u.kullaniciAdi || u.telefon || t("mgmt.notif.noUserInfo")}
                        </span>
                        <time className="adminUserNotifyUserDate">
                          {tr ? "Üyelik:" : "Joined:"} {otomasyonTarihFormatla(u.createdAt)}
                        </time>
                      </span>
                      <span className="adminUserNotifyUserTech" aria-label={tr ? "Kullanıcı teknik bilgileri" : "User technical information"}>
                        <span className="adminUserTechItem isPlatform">{adminUserPlatformLabel(u)}</span>
                        <span className={`adminUserTechItem ${adminUserPushReady(u) ? "isPush" : "isInApp"}`}>
                          {adminUserPushReady(u) ? "PUSH" : "IN-APP"}
                        </span>
                        <span className="adminUserTechItem isLanguage">{String(u.locale || "tr").slice(0, 2).toUpperCase()}</span>
                        <span className="adminUserTechItem isVersion" title={u.appVersion || (tr ? "Sürüm bilinmiyor" : "Unknown version")}>
                          {u.appVersion || "—"}
                        </span>
                        <span className={`adminUserTechItem isPlan ${adminUserIsPremium(u) ? "premium" : "free"}`}>
                          <span aria-hidden="true">{adminUserIsPremium(u) ? "◆" : "○"}</span>
                          <span>{adminUserIsPremium(u) ? "Premium" : (tr ? "Ücretsiz" : "Free")}</span>
                        </span>
                        <time
                          className="adminUserTechItem isActivity"
                          dateTime={u.lastActiveAt || undefined}
                          title={formatAdminExactActivity(u.lastActiveAt, lang)}
                        >
                          {formatAdminLastActive(u.lastActiveAt, lang)}
                        </time>
                      </span>
                    </button>
                  );
                })}
                {!globalKullanicilarYukleniyor && filtreliGlobalKullanicilar.length === 0 && (
                  <div className="adminUserEmptyResult">
                    <span aria-hidden="true">⌕</span>
                    <strong>{tr ? "Eşleşen kullanıcı bulunamadı" : "No matching users found"}</strong>
                    <small>{tr ? "Aramayı veya filtreleri değiştirin." : "Change the search or filters."}</small>
                  </div>
                )}
              </div>
            </>
          )}

          {!bildirimYonetebilir && (
            <div
              className="darkCard"
              style={{ padding: "10px", marginBottom: "10px" }}
            >
              <p className="ayarAciklama" style={{ margin: 0 }}>
                {t("mgmt.onlyFamily")}
                {t("mgmt.selectedFamilyOnly", { count: seciliUidler.length })}
              </p>
            </div>
          )}

          <div className="inputLabelGroup" style={{ marginBottom: "10px" }}>
            <label className="darkLabel">{t("mgmt.templates")}</label>

            <div style={{ display: "grid", gap: "8px" }}>
              {kullaniciHazirMesajlari.map((mesaj) => (
                <div className="adminTemplateEditable" key={mesaj.id}>
                  <button type="button" className="darkSecondaryBtn" onClick={() => hazirMesajSec(mesaj)}>
                    <strong>{mesaj.icon} {mesaj.baslik}</strong><small>{mesaj.aciklama}</small>
                  </button>
                  <div><button type="button" onClick={() => { hazirMesajSec(mesaj); setDuzenlenenKullaniciSablonId(mesaj.id); }}>{tr ? "Düzenle" : "Edit"}</button><button type="button" onClick={async () => { if (await appConfirm(tr ? "Bu hazır mesaj silinsin mi?" : "Delete this template?", { danger:true })) sablonListesiKaydet("userTemplates", kullaniciHazirMesajlari.filter((x) => x.id !== mesaj.id)); }}>{tr ? "Sil" : "Delete"}</button></div>
                </div>
              ))}
            </div>
          </div>

          <div className="inputLabelGroup" style={{ marginBottom: "10px" }}>
            <label className="darkLabel">{t("mgmt.title")}</label>
            <input
              className="darkInput"
              value={kullaniciBaslik}
              onChange={(e) => setKullaniciBaslik(e.target.value)}
              placeholder={t("mgmt.notif.userTitlePh")}
            />
          </div>

          <div className="inputLabelGroup" style={{ marginBottom: "10px" }}>
            <label className="darkLabel">{t("mgmt.field.message")}</label>
            <textarea
              className="darkInput"
              value={kullaniciMesaj}
              onChange={(e) => setKullaniciMesaj(e.target.value)}
              placeholder={t("mgmt.notif.userBodyPh")}
              rows={3}
            />
          </div>
          <button type="button" className="darkSecondaryBtn adminSaveTemplateBtn" disabled={!kullaniciBaslik.trim() || !kullaniciMesaj.trim()} onClick={() => { const mevcut = kullaniciHazirMesajlari.find((x) => x.id === duzenlenenKullaniciSablonId); const yeni = gecerliSablonOlustur(kullaniciBaslik, kullaniciMesaj, mevcut); sablonListesiKaydet("userTemplates", mevcut ? kullaniciHazirMesajlari.map((x) => x.id === mevcut.id ? { ...yeni, id:x.id } : x) : [...kullaniciHazirMesajlari, yeni]); setDuzenlenenKullaniciSablonId(null); }}>{duzenlenenKullaniciSablonId ? (tr ? "Hazır kartı güncelle" : "Update template") : (tr ? "Bu mesajı hazır kart olarak kaydet" : "Save as a message template")}</button>

          {bildirimYonetebilir && (
            <div
              className="darkCard"
              style={{
                padding: "12px",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gap: "10px",
                }}
              >
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.sendType")}</label>

                  <select
                    className="darkInput"
                    value={bildirimModu}
                    onChange={(e) => setBildirimModu(e.target.value)}
                  >
                    <option value="now">{t("mgmt.send.now")}</option>
                    <option value="scheduled">{t("mgmt.send.scheduled")}</option>
                  </select>
                </div>

                {bildirimModu === "scheduled" && (
                  <>
                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("mgmt.field.dateTime")}</label>

                      <input
                        type="datetime-local"
                        className="darkInput"
                        value={planlananTarih}
                        onChange={(e) => setPlanlananTarih(e.target.value)}
                      />
                    </div>

                    <div className="inputLabelGroup">
                      <label className="darkLabel">{t("mgmt.field.repeatType")}</label>

                      <select
                        className="darkInput"
                        value={tekrarTipi}
                        onChange={(e) => setTekrarTipi(e.target.value)}
                      >
                        <option value="none">{t("mgmt.repeat.none")}</option>
                        <option value="daily">{t("mgmt.repeat.daily")}</option>
                        <option value="weekly">{t("mgmt.repeat.weekly")}</option>
                        <option value="monthly">{t("mgmt.repeat.monthly")}</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <button
            className="primaryButton"
            onClick={
              bildirimYonetebilir && bildirimModu === "scheduled"
                ? zamanliBildirimKaydet
                : kullanicilaraBildirimGonder
            }
            disabled={kullaniciBildirimGonderiliyor}
          >
            {kullaniciBildirimGonderiliyor
              ? t("mgmt.notif.sending")
              : t("mgmt.notif.sendToSelected").replace("{n}", seciliUidler.length)}
          </button>

          {kullaniciBildirimDurum && (
            <div
              className={
                kullaniciBildirimDurum.includes("✓")
                  ? "profilSuccess"
                  : "profilError"
              }
              style={{ marginTop: "8px" }}
            >
              {kullaniciBildirimDurum}
            </div>
          )}
        </div>
      )}

      {bildirimYonetebilir && (
        <>
          <button
            className="darkSecondaryBtn"
            onClick={sistemDuyuruPanelToggle}
          >
            {sistemDuyuruPanelAcik
              ? t("mgmt.sys.close")
              : t("mgmt.sys.open")}
          </button>

          {sistemDuyuruPanelAcik && (
            <div className="darkCard systemAnnouncementPanel">
              <div className="systemAnnouncementHeader">
                <div>
                  <strong>{t("mgmt.notify.systemAnnouncement")}</strong>
                  <p className="ayarAciklama">
                    {t("mgmt.globalIndependent")}
                    {t("mgmt.totalUsers", { count: globalKullanicilar.length })}
                  </p>
                </div>
                <button className="ayarKapatBtn" type="button" onClick={() => setSistemDuyuruPanelAcik(false)}>×</button>
              </div>

              <div className="inputLabelGroup">
                <label className="darkLabel">{t("mgmt.systemTemplates")}</label>
                <div
                  className="systemAnnouncementTemplateGrid adminScrollTemplates3"
                  style={{
                    gridTemplateColumns: "1fr",
                    paddingRight: "4px",
                  }}
                >
                  {sistemHazirMesajlari.map((mesaj) => (
                    <div className="adminTemplateEditable" key={mesaj.id}>
                      <button type="button" className="systemAnnouncementTemplateCard" onClick={() => sistemDuyuruKartiSec(mesaj)}>
                        <span>{mesaj.icon}</span><strong>{mesaj.baslik}</strong><small>{mesaj.aciklama}</small>
                      </button>
                      <div><button type="button" onClick={() => { sistemDuyuruKartiSec(mesaj); setDuzenlenenSistemSablonId(mesaj.id); }}>{tr ? "Düzenle" : "Edit"}</button><button type="button" onClick={async () => { if (await appConfirm(tr ? "Bu hazır mesaj silinsin mi?" : "Delete this template?", { danger:true })) sablonListesiKaydet("systemTemplates", sistemHazirMesajlari.filter((x) => x.id !== mesaj.id)); }}>{tr ? "Sil" : "Delete"}</button></div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="familyNotifyFormGrid">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.title")} (TR)</label>
                  <input
                    className="darkInput"
                    value={sistemDuyuruBaslik}
                    onChange={(e) => handleSistemDuyuruBaslikChange(e.target.value)}
                    placeholder={t("mgmt.sys.titlePlaceholder")}
                  />
                </div>

                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("mgmt.sendType")}</label>
                  <select
                    className="darkInput"
                    value={sistemDuyuruModu}
                    onChange={(e) => setSistemDuyuruModu(e.target.value)}
                  >
                    <option value="now">{t("mgmt.send.now")}</option>
                    <option value="scheduled">{t("mgmt.send.scheduledAlt")}</option>
                  </select>
                </div>
              </div>

              <div className="inputLabelGroup">
                <label className="darkLabel">{t("mgmt.field.message")} (TR)</label>
                <textarea
                  className="darkInput"
                  value={sistemDuyuruMesaj}
                  onChange={(e) => handleSistemDuyuruMesajChange(e.target.value)}
                  rows={3}
                  placeholder={t("mgmt.sys.bodyPlaceholder")}
                />
              </div>
              <button type="button" className="darkSecondaryBtn adminSaveTemplateBtn" disabled={!sistemDuyuruBaslik.trim() || !sistemDuyuruMesaj.trim()} onClick={() => { const mevcut = sistemHazirMesajlari.find((x) => x.id === duzenlenenSistemSablonId); const yeni = gecerliSablonOlustur(sistemDuyuruBaslik, sistemDuyuruMesaj, mevcut); sablonListesiKaydet("systemTemplates", mevcut ? sistemHazirMesajlari.map((x) => x.id === mevcut.id ? { ...yeni, id:x.id } : x) : [...sistemHazirMesajlari, yeni]); setDuzenlenenSistemSablonId(null); }}>{duzenlenenSistemSablonId ? (tr ? "Hazır kartı güncelle" : "Update template") : (tr ? "Bu duyuruyu hazır kart olarak kaydet" : "Save as an announcement template")}</button>

              {sistemDuyuruSecilenSablon ? (
                <p className="ayarAciklama systemAnnouncementLangNote">
                  {t("mgmt.sys.templateAutoTranslate")}
                </p>
              ) : (
                <>
                  <p className="ayarAciklama systemAnnouncementLangNote">
                    {t("mgmt.sys.fillEnglishHint")}
                  </p>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.title")} (EN)</label>
                    <input
                      className="darkInput"
                      value={sistemDuyuruBaslikEn}
                      onChange={(e) => setSistemDuyuruBaslikEn(e.target.value)}
                      placeholder={t("mgmt.sys.titlePlaceholderEn")}
                    />
                  </div>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.field.message")} (EN)</label>
                    <textarea
                      className="darkInput"
                      value={sistemDuyuruMesajEn}
                      onChange={(e) => setSistemDuyuruMesajEn(e.target.value)}
                      rows={3}
                      placeholder={t("mgmt.sys.bodyPlaceholderEn")}
                    />
                  </div>
                  {!(sistemDuyuruBaslikEn.trim() && sistemDuyuruMesajEn.trim()) && (
                    <p className="ayarAciklama systemAnnouncementLangWarning">
                      {t("mgmt.sys.noEnglishWarning")}
                    </p>
                  )}
                </>
              )}

              {sistemDuyuruModu === "scheduled" && (
                <div className="familyScheduledEditGrid">
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.field.dateTime")}</label>
                    <input
                      type="datetime-local"
                      className="darkInput"
                      value={sistemDuyuruPlanlananTarih}
                      onChange={(e) => setSistemDuyuruPlanlananTarih(e.target.value)}
                    />
                  </div>

                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.field.repeatType")}</label>
                    <select
                      className="darkInput"
                      value={sistemDuyuruTekrarTipi}
                      onChange={(e) => setSistemDuyuruTekrarTipi(e.target.value)}
                    >
                      {getSistemDuyuruTekrarTipleri(t).map((tekrar) => (
                        <option key={tekrar.value} value={tekrar.value}>{tekrar.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div className="systemAnnouncementFilterGrid">
                <div className="inputLabelGroup">
                  <label className="darkLabel">{tr ? "Kullanıcı dili" : "User language"}</label>
                  <select className="darkInput" value={sistemDuyuruDilFiltresi} onChange={(e) => setSistemDuyuruDilFiltresi(e.target.value)}>
                    <option value="all">{tr ? "Tüm diller" : "All languages"}</option>
                    <option value="tr">Türkçe</option><option value="en">English</option>
                  </select>
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{tr ? "Platform" : "Platform"}</label>
                  <select className="darkInput" value={sistemDuyuruPlatformFiltresi} onChange={(e) => setSistemDuyuruPlatformFiltresi(e.target.value)}>
                    <option value="all">{tr ? "Tüm platformlar" : "All platforms"}</option>
                    <option value="ios">iOS</option><option value="android">Android</option>
                  </select>
                </div>
              </div>

              <div className="systemAnnouncementSummary">
                <span>{t("mgmt.notify.target")}</span>
                <strong>{sistemDuyuruHedefleri.length}</strong>
              </div>
              <div className="systemAnnouncementSummary systemAnnouncementLangBreakdown">
                <span>{t("mgmt.sys.langBreakdown")}</span>
                <strong>
                  🇹🇷 {sistemDuyuruHedefleri.filter((u) => (u.locale || "tr") !== "en").length}
                  {"  ·  "}
                  🇬🇧 {sistemDuyuruHedefleri.filter((u) => u.locale === "en").length}
                </strong>
              </div>
              <div className="systemAnnouncementSummary systemAnnouncementLangBreakdown">
                <span>{tr ? "Platform dağılımı" : "Platform distribution"}</span>
                <strong>iOS {sistemDuyuruHedefleri.filter((u) => kullaniciPlatformdaMi(u, "ios")).length}{"  ·  "}Android {sistemDuyuruHedefleri.filter((u) => kullaniciPlatformdaMi(u, "android")).length}</strong>
              </div>

              <button
                className="primaryButton"
                onClick={sistemDuyurusuGonderClick}
                disabled={sistemDuyuruGonderiliyor}
              >
                {sistemDuyuruGonderiliyor
                  ? t("mgmt.notif.sending")
                  : sistemDuyuruModu === "scheduled"
                    ? t("mgmt.sys.scheduleSelected", { count: sistemDuyuruHedefleri.length })
                    : t("mgmt.sys.sendSelected", { count: sistemDuyuruHedefleri.length })}
              </button>

              {sistemDuyuruDurum && (
                <div
                  className={sistemDuyuruDurum.includes("✓") ? "profilSuccess" : "profilError"}
                  style={{ marginTop: "8px" }}
                >
                  {sistemDuyuruDurum}
                </div>
              )}
            </div>
          )}

          <button className="darkSecondaryBtn" onClick={otomatikKurallarPanelToggle}>
            {otomatikKurallarAcik ? t("mgmt.auto.close") : t("mgmt.auto.open")}
          </button>

          {otomatikKurallarAcik && (
            <div className="automationRulesPanel">
              <div className="automationPanelHeader">
                <div>
                  <strong>{t("mgmt.notify.automation")}</strong>
                  <p>
                    {t("mgmt.cronInfo")}
                  </p>
                </div>
                <button className="ayarKapatBtn" onClick={() => setOtomatikKurallarAcik(false)}>×</button>
              </div>

              <div className="automationStatusGrid">
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.lastRun")}</span>
                  <strong>{otomasyonTarihFormatla(otomatikSonCalisma?.createdAt)}</strong>
                </div>
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.result")}</span>
                  <strong>{otomatikSonCalisma?.status || "-"}</strong>
                </div>
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.sentPush")}</span>
                  <strong>
                    {otomatikSonCalisma?.totalSent ?? 0}
                    <button type="button" className="automationStatusInlineMeta" onClick={son24SaatPushDetayToggle} aria-expanded={son24SaatDetayAcik}>
                      {tr ? "24 sa" : "24h"}: {bildirimOtomasyonOzet.son24SaatGonderilen ?? 0}
                    </button>
                  </strong>
                </div>
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.error")}</span>
                  <strong>
                    {otomatikSonCalisma?.totalFailed ?? 0}
                    <small className="automationStatusInlineMeta error">
                      {tr ? "24 sa" : "24h"}: {bildirimOtomasyonOzet.son24SaatHata ?? 0}
                    </small>
                  </strong>
                </div>
              </div>

              {son24SaatDetayAcik && (
                <div className="automation24HourDetails">
                  <div className="familySectionHeader">
                    <strong>{tr ? "Son 24 saatlik push gönderimleri" : "Push deliveries in the last 24 hours"}</strong>
                    <span className="automationTinyInfo">{son24SaatPushlari.total}</span>
                  </div>
                  <div className="automation24HourList">
                    {son24SaatPushYukleniyor && !son24SaatPushlari.items.length ? (
                      <small>{tr ? "Gönderimler yükleniyor…" : "Loading deliveries…"}</small>
                    ) : !son24SaatPushlari.items.length ? (
                      <small>{tr ? "Gönderim ayrıntısı bulunamadı." : "No delivery detail found."}</small>
                    ) : son24SaatPushlari.items.map((delivery) => (
                      <div key={delivery.id} className="automationDeliveryItem">
                        <div><strong>{delivery.recipientName || (tr ? "Kullanıcı" : "User")}</strong><small>{delivery.title || "-"}</small></div>
                        <time>{otomasyonTarihFormatla(delivery.createdAt)}</time>
                      </div>
                    ))}
                  </div>
                  {son24SaatPushlari.items.length < son24SaatPushlari.total && (
                    <button type="button" className="darkSecondaryBtn mini" disabled={son24SaatPushYukleniyor} onClick={dahaFazlaSon24SaatPushYukle}>
                      {tr ? "100 kayıt daha getir" : "Load 100 more"}
                    </button>
                  )}
                </div>
              )}

              <button
                className="primaryButton"
                onClick={otomatikBildirimleriSimdiKontrolEt}
                disabled={otomatikSimdiCalisiyor}
              >
                {otomatikSimdiCalisiyor ? t("mgmt.auto.running") : t("mgmt.auto.runNow")}
              </button>

              {otomatikDurum && (
                <div className={otomatikDurum.includes("✓") ? "profilSuccess" : "profilError"}>
                  {otomatikDurum}
                </div>
              )}

              <div className="automationRuleGrid">
                {otomatikKurallar.map((rule) => (
                  <div key={rule.key} className={`automationRuleCard ${rule.enabled ? "active" : "passive"}`}>
                    <div className="automationRuleHeader">
                      <div>
                        <strong>{rule.icon} {rule.title}</strong>
                        <small>{rule.description}</small>
                      </div>
                      <div
                        className={`toggleSwitch ${rule.enabled ? "on" : ""}`}
                        onClick={() => otomatikKuralToggle(rule)}
                        title={rule.enabled ? "Pasif yap" : "Aktif yap"}
                      >
                        <div className="toggleThumb" />
                      </div>
                    </div>

                    <div className="automationOptionGrid">
                      {Object.entries(rule.options || {}).map(([optionKey, value]) => (
                        <label key={optionKey} className="automationOptionRow">
                          <span>{getOtomatikKuralOptionLabels(t)[optionKey] || optionKey}</span>
                          <div
                            className={`toggleSwitch sm ${value ? "on" : ""}`}
                            onClick={() => otomatikKuralOptionToggle(rule, optionKey)}
                          >
                            <div className="toggleThumb" />
                          </div>
                        </label>
                      ))}
                    </div>

                    <div className="automationRuleMeta">
                      <span>{t("mgmt.lastRun", { date: otomasyonTarihFormatla(rule.lastRunAt) })}</span>
                      <span>
                        {t("mgmt.pushLabel")}:{" "}
                        <span style={{ color: (rule.lastSentCount ?? 0) > 0 ? "#22c55e" : "inherit", fontWeight: (rule.lastSentCount ?? 0) > 0 ? 700 : 400 }}>
                          {rule.lastSentCount ?? 0}
                        </span>
                        {" · "}
                        {t("mgmt.errorLabel2")}:{" "}
                        <span style={{ color: (rule.lastFailedCount ?? 0) > 0 ? "#ef4444" : "inherit", fontWeight: (rule.lastFailedCount ?? 0) > 0 ? 700 : 400 }}>
                          {rule.lastFailedCount ?? 0}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="automationScheduledBox">
                <div className="familySectionHeader">
                  <strong>{t("mgmt.notify.repeatAnnouncements")}</strong>
                  <span className="automationTinyInfo">{t("mgmt.activeRecords", { count: otomatikTekrarDuyurular.length })}</span>
                </div>

                {otomatikTekrarDuyurular.length === 0 ? (
                  <div className="emptyState">{t("mgmt.noRepeatingAnnouncement")}</div>
                ) : (
                  <div className="automationLogList">
                    {otomatikTekrarDuyurular.map((item) => (
                      <div key={item.id} className="automationLogItem automationScheduledItem">
                        <div>
                          <strong>{item.baslik || t("mgmt.notif.untitledSys")}</strong>
                          <small>{item.aciklama || t("mgmt.noMessage")}</small>
                          <small>
                            Tekrar: {item.tekrarTipi || "-"} · {otomasyonTarihFormatla(item.planlananTarih)} · {item.durum || t("mgmt.notify.statusPending")}
                          </small>
                        </div>
                        <div className="automationScheduledActions">
                          <button className="darkSecondaryBtn" onClick={() => tekrarEdenSistemDuyurusuDurumToggle(item)}>
                            {item.durum === "duraklatildi" ? t("mgmt.auto.resume") : t("mgmt.auto.pause")}
                          </button>
                          <button className="darkSecondaryBtn danger" onClick={() => tekrarEdenSistemDuyurusuIptalClick(item)}>
                            {t("mgmt.cancel")}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="automationLogsBox">
                <div className="familySectionHeader">
                  <strong>{t("mgmt.notify.recentLogs")}</strong>
                  <button className="darkSecondaryBtn" onClick={() => otomatikKurallariYukle({ panelAc: false })}>
                    {otomatikYukleniyor ? t("mgmt.auto.loading") : t("mgmt.auto.refresh")}
                  </button>
                </div>

                {otomatikLoglar.length === 0 ? (
                  <div className="emptyState">{t("mgmt.noAutomationLog")}</div>
                ) : (
                  <div className="automationLogList adminScrollRuleLogs5">
                    {otomatikLoglar.map((log) => {
                      const pushCount = log.totalSent ?? 0;
                      const errorCount = log.totalFailed ?? 0;
                      return (
                        <div key={log.id} className="automationLogItem">
                          <strong>{log.title || t("mgmt.notif.untitledAuto")}</strong>
                          <small>
                            {otomasyonTarihFormatla(log.createdAt)} · Durum: {log.status || "-"}
                          </small>
                          <small>
                            {t("mgmt.created", { created: log.totalCreated ?? 0 })}
                            {" · "}
                            {t("mgmt.pushLabel")}:{" "}
                            <button
                              type="button"
                              className={`automationPushCount ${pushCount > 0 ? "active" : ""}`}
                              disabled={pushCount < 1}
                              onClick={() => otomatikLogDetayToggle(log)}
                              aria-expanded={otomatikLogDetayId === log.id}
                            >{pushCount}</button>
                            {" · "}
                            {t("mgmt.errorLabel2")}:{" "}
                            <span style={{ color: errorCount > 0 ? "#ef4444" : "inherit", fontWeight: errorCount > 0 ? 700 : 400 }}>
                              {errorCount}
                            </span>
                          </small>
                          {otomatikLogDetayId === log.id && (
                            <div className="automationDeliveryDetails">
                              {otomatikLogDetayYukleniyor === log.id ? (
                                <small>{tr ? "Gönderimler yükleniyor…" : "Loading deliveries…"}</small>
                              ) : (otomatikLogDetaylari[log.id] || []).length === 0 ? (
                                <small>{tr ? "Bu eski çalışma için alıcı ayrıntısı bulunmuyor." : "Recipient details are unavailable for this older run."}</small>
                              ) : (
                                (otomatikLogDetaylari[log.id] || []).map((delivery) => (
                                  <div key={delivery.id} className="automationDeliveryItem">
                                    <div>
                                      <strong>{delivery.recipientName || (tr ? "Kullanıcı" : "User")}</strong>
                                      <small>{delivery.title || "-"}</small>
                                    </div>
                                    <time>{otomasyonTarihFormatla(delivery.createdAt)}</time>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          <button className="darkSecondaryBtn" onClick={bildirimOtomasyonPanelToggle}>
            {bildirimOtomasyonPanelAcik ? t("mgmt.auto.panelClose") : t("mgmt.auto.panelOpen")}
          </button>

          {bildirimOtomasyonPanelAcik && (
            <div className="automationRulesPanel">
              <div className="automationPanelHeader">
                <div>
                  <strong>{t("mgmt.notify.automationTitle")}</strong>
                  <p>
                    {t("mgmt.policyDesc")}
                  </p>
                </div>
                <button className="ayarKapatBtn" onClick={() => setBildirimOtomasyonPanelAcik(false)}>×</button>
              </div>

              <div className="automationStatusGrid">
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.activeScheduled")}</span>
                  <strong>{bildirimOtomasyonOzet.aktifZamanli ?? 0}</strong>
                </div>
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.pendingNotif")}</span>
                  <strong>{bildirimOtomasyonOzet.bekleyen ?? 0}</strong>
                </div>
                <div className="automationStatusCard">
                  <span>{t("mgmt.notify.todaySent")}</span>
                  <strong>{bildirimOtomasyonOzet.bugunGonderilen ?? 0}</strong>
                </div>
              </div>

              <div className="automationScheduledBox">
                <div className="familySectionHeader">
                  <strong>{t("mgmt.notify.quietHours")}</strong>
                  <button className="darkSecondaryBtn" onClick={bildirimGlobalPolicyKaydet} disabled={bildirimOtomasyonKaydediliyor === "global"}>
                    {bildirimOtomasyonKaydediliyor === "global" ? t("management.saving") : t("management.save")}
                  </button>
                </div>

                <label className="automationOptionRow">
                  <span>{t("mgmt.notify.applyQuiet")}</span>
                  <div
                    className={`toggleSwitch sm ${bildirimPolitikaGlobal.globalQuietHoursEnabled ? "on" : ""}`}
                    onClick={() => bildirimGlobalPolicyGuncelle("globalQuietHoursEnabled", !bildirimPolitikaGlobal.globalQuietHoursEnabled)}
                  >
                    <div className="toggleThumb" />
                  </div>
                </label>

                <div className="familyScheduledEditGrid">
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.quietStart")}</label>
                    <input
                      type="time"
                      className="darkInput"
                      value={bildirimPolitikaGlobal.quietStart || "22:00"}
                      onChange={(e) => bildirimGlobalPolicyGuncelle("quietStart", e.target.value)}
                    />
                  </div>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.quietEnd")}</label>
                    <input
                      type="time"
                      className="darkInput"
                      value={bildirimPolitikaGlobal.quietEnd || "08:00"}
                      onChange={(e) => bildirimGlobalPolicyGuncelle("quietEnd", e.target.value)}
                    />
                  </div>
                  <div className="inputLabelGroup">
                    <label className="darkLabel">{t("mgmt.cronInterval")}</label>
                    <input
                      type="number"
                      className="darkInput"
                      value="1"
                      readOnly
                      aria-readonly="true"
                    />
                  </div>
                </div>
                <p className="automationHint">{t("mgmt.localTimeHint")}</p>
              </div>

              <div className="familySectionHeader" style={{ marginTop: "14px" }}>
                <strong>{t("mgmt.notify.policyTitle")}</strong>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <button className="darkSecondaryBtn" onClick={() => bildirimOtomasyonunuYukle({ panelAc: false })}>
                    {bildirimOtomasyonYukleniyor ? t("mgmt.auto.loading") : t("mgmt.auto.refresh")}
                  </button>
                  <button className="darkSecondaryBtn danger" onClick={bildirimPolitikalariVarsayilanaDonClick} disabled={bildirimOtomasyonKaydediliyor === "defaults"}>
                    {t("mgmt.resetDefaults")}
                  </button>
                </div>
              </div>

              <div className="notificationPolicyQuickNav">
                {bildirimPolitikalari.map((policy) => (
                  <button
                    key={policy.key}
                    type="button"
                    className={`notificationPolicyChip ${bildirimPolitikaAktifKey === policy.key ? "active" : ""}`}
                    onClick={() => setBildirimPolitikaAktifKey(policy.key)}
                  >
                    <span>{policy.icon}</span>
                    <strong>{policy.title}</strong>
                  </button>
                ))}
                <button
                  type="button"
                  className={`notificationPolicyChip all ${bildirimPolitikaAktifKey === "all" ? "active" : ""}`}
                  onClick={() => setBildirimPolitikaAktifKey("all")}
                >
                  <span>🧩</span>
                  <strong>{t("common.all")}</strong>
                </button>
              </div>

              {bildirimOtomasyonDurum && (
                <div className={bildirimOtomasyonDurum.includes("✓") ? "profilSuccess" : "profilError"}>
                  {bildirimOtomasyonDurum}
                </div>
              )}

              <div className="automationRuleGrid notificationPolicyFocusedGrid">
                {(bildirimPolitikaAktifKey === "all" ? bildirimPolitikalari : bildirimPolitikalari.filter((policy) => policy.key === bildirimPolitikaAktifKey)).map((policy) => (
                  <div key={policy.key} className={`automationRuleCard ${policy.enabled ? "active" : "passive"}`}>
                    <div className="automationRuleHeader">
                      <div>
                        <strong>{policy.icon} {policy.title}</strong>
                        <small>{policy.description}</small>
                      </div>
                      <div
                        className={`toggleSwitch ${policy.enabled ? "on" : ""}`}
                        onClick={() => bildirimPolitikaLocalGuncelle(policy.key, "enabled", !policy.enabled)}
                      >
                        <div className="toggleThumb" />
                      </div>
                    </div>

                    <div className="familyScheduledEditGrid">
                      <div className="inputLabelGroup">
                        <label className="darkLabel">{t("mgmt.dailyLimit")}</label>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          className="darkInput"
                          value={policy.dailyLimit || 1}
                          onChange={(e) => bildirimPolitikaLocalGuncelle(policy.key, "dailyLimit", Number(e.target.value || 1))}
                        />
                      </div>
                      <label className="automationOptionRow">
                        <span>{t("mgmt.notify.respectQuiet")}</span>
                        <div
                          className={`toggleSwitch sm ${policy.respectGlobalQuietHours !== false ? "on" : ""}`}
                          onClick={() => bildirimPolitikaLocalGuncelle(policy.key, "respectGlobalQuietHours", policy.respectGlobalQuietHours === false)}
                        >
                          <div className="toggleThumb" />
                        </div>
                      </label>
                      {policy.key === "financeReminder" && (
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("mgmt.finance.deliverySpread")}</label>
                          <input
                            type="number"
                            min="0"
                            max="360"
                            className="darkInput"
                            value={policy.deliverySpreadMinutes ?? 120}
                            onChange={(e) => bildirimPolitikaLocalGuncelle(policy.key, "deliverySpreadMinutes", Number(e.target.value || 0))}
                          />
                        </div>
                      )}
                      <label className="automationOptionRow">
                        <span>{t("mgmt.notify.blockRepeat")}</span>
                        <div
                          className={`toggleSwitch sm ${policy.preventDuplicates !== false ? "on" : ""}`}
                          onClick={() => bildirimPolitikaLocalGuncelle(policy.key, "preventDuplicates", policy.preventDuplicates === false)}
                        >
                          <div className="toggleThumb" />
                        </div>
                      </label>
                    </div>

                    <div className="automationOptionGrid">
                      {(policy.rules || []).map((rule) => (
                        <div key={rule.key} className="automationLogItem" style={{ gap: "10px" }}>
                          <label className="automationOptionRow" style={{ marginBottom: 0 }}>
                            <span>
                              <strong>{rule.label || "Kural"}</strong>
                              <small style={{ display: "block", color: "#94a3b8", marginTop: 2 }}>{policyRuleSubText(rule, t)}</small>
                            </span>
                            <div
                              className={`toggleSwitch sm ${rule.enabled !== false ? "on" : ""}`}
                              onClick={() => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "enabled", rule.enabled === false)}
                            >
                              <div className="toggleThumb" />
                            </div>
                          </label>

                          <div className="familyScheduledEditGrid">
                            <div className="inputLabelGroup">
                              <label className="darkLabel">{t("mgmt.ruleName")}</label>
                              <input
                                className="darkInput"
                                value={policyRuleValue(rule, "label", "")}
                                onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "label", e.target.value)}
                              />
                            </div>

                            {policy.key === "financeReminder" && rule.financeKind && (
                              <div className="inputLabelGroup">
                                <label className="darkLabel">{t("mgmt.finance.recordType")}</label>
                                <select className="darkInput" value={rule.financeKind} onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "financeKind", e.target.value)}>
                                  <option value="installment">{t("mgmt.auto.finance.installments")}</option>
                                  <option value="subscription">{t("mgmt.auto.finance.subscriptions")}</option>
                                  <option value="bill">{t("mgmt.auto.finance.bills")}</option>
                                </select>
                              </div>
                            )}

                            {policy.key === "financeReminder" && String(rule.type || "").startsWith("saving") && (
                              <>
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">{t("mgmt.finance.inactivityDays")}</label>
                                  <input type="number" min="1" max="730" className="darkInput" value={rule.thresholdDays || 30} onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "thresholdDays", Number(e.target.value || 1))} />
                                </div>
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">{t("mgmt.finance.repeatEveryDays")}</label>
                                  <input type="number" min="1" max="365" className="darkInput" value={rule.repeatEveryDays || 14} onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "repeatEveryDays", Number(e.target.value || 1))} />
                                </div>
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">{t("mgmt.finance.maxRepeat")}</label>
                                  <input type="number" min="1" max="20" className="darkInput" value={rule.maxRepeat || 3} onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "maxRepeat", Number(e.target.value || 1))} />
                                </div>
                              </>
                            )}

                            {rule.usesOwnTime ? (
                              <>
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">Erteleme dk</label>
                                  <input
                                    type="number"
                                    min="1"
                                    max="120"
                                    className="darkInput"
                                    value={rule.snoozeMinutes || 10}
                                    onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "snoozeMinutes", Number(e.target.value || 10))}
                                  />
                                </div>
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">Maks tekrar</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="10"
                                    className="darkInput"
                                    value={rule.maxRepeat || 3}
                                    onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "maxRepeat", Number(e.target.value || 3))}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                {typeof rule.daysBefore === "number" && (
                                  <div className="inputLabelGroup">
                                    <label className="darkLabel">{t("mgmt.daysBefore")}</label>
                                    <input
                                      type="number"
                                      min="0"
                                      max="365"
                                      className="darkInput"
                                      value={rule.daysBefore}
                                      onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "daysBefore", Number(e.target.value || 0))}
                                    />
                                  </div>
                                )}
                                <div className="inputLabelGroup">
                                  <label className="darkLabel">{t("mgmt.deliveryTime")}</label>
                                  <input
                                    type="time"
                                    className="darkInput"
                                    value={rule.time || "09:00"}
                                    onChange={(e) => bildirimPolitikaRuleGuncelle(policy.key, rule.key, "time", e.target.value)}
                                  />
                                </div>
                              </>
                            )}
                          </div>

                          {rule.custom && (
                            <button className="darkSecondaryBtn danger" onClick={() => bildirimPolitikaRuleSil(policy.key, rule.key)}>
                              {t("mgmt.deleteRule")}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {["documentExpiry", "financeReminder"].includes(policy.key) && (
                      <button className="darkSecondaryBtn" onClick={() => bildirimPolitikaRuleEkle(policy.key)}>
                        {policy.key === "financeReminder" ? t("mgmt.finance.addReminderDay") : t("mgmt.addDayRule")}
                      </button>
                    )}

                    <button
                      className="primaryButton"
                      onClick={() => bildirimPolitikaKaydetClick(policy)}
                      disabled={bildirimOtomasyonKaydediliyor === policy.key}
                    >
                      {bildirimOtomasyonKaydediliyor === policy.key ? t("management.saving") : t("management.save")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button className="darkSecondaryBtn" onClick={zamanlanmisAdminPanelToggle}>
            {zamanlanmisAdminPanelAcik ? t("mgmt.notify.scheduled2") + " ✕" : t("mgmt.notify.scheduled2")}
          </button>

          {zamanlanmisAdminPanelAcik && (
            <div className="scheduledAdminPanel">
              <div className="scheduledAdminHeader">
                <div>
                  <strong>{t("mgmt.notify.scheduled2")}</strong>
                  <p>
                    {t("mgmt.scheduledInfo")}
                  </p>
                </div>
                <button className="ayarKapatBtn" onClick={() => setZamanlanmisAdminPanelAcik(false)}>×</button>
              </div>

              <div className="scheduledAdminToolbar">
                <div className="scheduledAdminFilters">
                  {getZamanlanmisBildirimFiltreleri(t).map((f) => (
                    <button
                      key={f.value}
                      type="button"
                      className={`scheduledAdminFilterBtn ${zamanlanmisFiltre === f.value ? "active" : ""}`}
                      onClick={() => { setZamanlanmisFiltre(f.value); setZamanlanmisGosterimAdedi(10); setZamanlanmisAcikId(null); }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                <button className="darkSecondaryBtn" onClick={() => zamanlanmisBildirimleriYukle({ panelAc: false })}>
                  {zamanlanmisYukleniyor ? t("mgmt.auto.loading") : t("mgmt.auto.refresh")}
                </button>
              </div>

              <div className="scheduledAdminSummaryGrid">
                <div className="scheduledAdminSummaryCard">
                  <span>{t("mgmt.notify.pendingTotal")}</span>
                  <strong>{zamanlanmisBildirimler.length}</strong>
                </div>
                <div className="scheduledAdminSummaryCard">
                  <span>{t("mgmt.notify.filtered")}</span>
                  <strong>{filtreliZamanlanmisBildirimler.length}</strong>
                </div>
                <div className="scheduledAdminSummaryCard">
                  <span>{t("mgmt.recurring")}</span>
                  <strong>{zamanlanmisBildirimler.filter((x) => x.tekrarTipi && x.tekrarTipi !== "none").length}</strong>
                </div>
              </div>

              {zamanlanmisDurum && (
                <div className={zamanlanmisDurum.includes("✓") ? "profilSuccess" : "profilError"}>
                  {zamanlanmisDurum}
                </div>
              )}

              {filtreliZamanlanmisBildirimler.length === 0 ? (
                <div className="emptyState">{t("mgmt.noScheduledFilter")}</div>
              ) : (
                <div className="scheduledAdminList">
                  {gorunenZamanlanmisBildirimler.map((item) => {
                    const detayAcik = zamanlanmisAcikId === item.id;
                    return (
                    <div key={item.id} className={`scheduledAdminItem ${detayAcik ? "expanded" : "compact"}`}>
                      <div className="scheduledAdminItemTop">
                        <div>
                          <strong>{item.baslik || t("mgmt.notif.untitled")}</strong>
                          <small>ID: {item.id}</small>
                        </div>
                        <div className="scheduledAdminTopActions">
                          <span className="scheduledAdminStatus">{item.durum || "bekliyor"}</span>
                          <button
                            type="button"
                            className="darkSecondaryBtn mini"
                            onClick={() => setZamanlanmisAcikId(detayAcik ? null : item.id)}
                          >
                            {detayAcik ? t("common.close") : t("common.edit")}
                          </button>
                        </div>
                      </div>

                      <div className="scheduledAdminMetaGrid">
                        <span><b>{t("mgmt.notify.targetLabel")}</b> {hedefTipiLabel(item, t)}</span>
                        <span><b>{t("mgmt.notify.targetCount")}</b> {hedefSayisiBul(item)}</span>
                        <span><b>{t("mgmt.notify.repeatLabel")}</b> {t(ZAMANLANMIS_TEKRAR_KEYS[item.tekrarTipi || "none"]) || item.tekrarTipi || "-"}</span>
                        <span><b>{t("mgmt.notify.planLabel")}</b> {otomasyonTarihFormatla(item.planlananTarih)}</span>
                        <span><b>{t("mgmt.notify.typeLabel")}</b> {item.tip || "-"}</span>
                        <span><b>{t("mgmt.notify.creatorLabel")}</b> {item.olusturanUid || "-"}</span>
                      </div>

                      {detayAcik && (
                        <div className="scheduledAdminEditPanel">
                      <div className="scheduledAdminEditGrid">
                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("mgmt.title")}</label>
                          <input
                            className="darkInput"
                            value={item.baslik || ""}
                            onChange={(e) => zamanlanmisBildirimLocalGuncelle(item.id, "baslik", e.target.value)}
                          />
                        </div>

                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("mgmt.field.plannedDateTime")}</label>
                          <input
                            type="datetime-local"
                            className="darkInput"
                            value={datetimeLocalValue(item.planlananTarih)}
                            onChange={(e) => zamanlanmisBildirimLocalGuncelle(item.id, "planlananTarih", e.target.value)}
                          />
                        </div>

                        <div className="inputLabelGroup">
                          <label className="darkLabel">{t("mgmt.field.repeatType")}</label>
                          <select
                            className="darkInput"
                            value={item.tekrarTipi || "none"}
                            onChange={(e) => zamanlanmisBildirimLocalGuncelle(item.id, "tekrarTipi", e.target.value)}
                          >
                            {getSistemDuyuruTekrarTipleri(t).map((tekrar) => (
                              <option key={tekrar.value} value={tekrar.value}>{tekrar.label}</option>
                            ))}
                            <option value="every3days">{t("mgmt.repeat.every3days")}</option>
                          </select>
                        </div>
                      </div>

                      <div className="inputLabelGroup">
                        <label className="darkLabel">{t("mgmt.field.message")}</label>
                        <textarea
                          className="darkInput scheduledAdminTextarea"
                          value={item.aciklama || ""}
                          onChange={(e) => zamanlanmisBildirimLocalGuncelle(item.id, "aciklama", e.target.value)}
                        />
                      </div>

                      <div className="scheduledAdminActions">
                        <button className="primaryButton" onClick={() => zamanlanmisBildirimiKaydetClick(item)}>
                          {t("common.save")}
                        </button>
                        <button className="darkSecondaryBtn" onClick={() => zamanlanmisBildirimiIptalClick(item)}>
                          {t("mgmt.cancel")}
                        </button>
                        <button className="darkSecondaryBtn danger" onClick={() => zamanlanmisBildirimiSilClick(item)}>
                          {t("common.delete")}
                        </button>
                      </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                  {dahaFazlaZamanlanmisVar && (
                    <button
                      type="button"
                      className="darkSecondaryBtn scheduledAdminMoreBtn"
                      onClick={() => setZamanlanmisGosterimAdedi((n) => n + 10)}
                    >
                      {t("common.showMore")} ({filtreliZamanlanmisBildirimler.length - gorunenZamanlanmisBildirimler.length})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {(
            <button className="darkSecondaryBtn" onClick={bildirimGecmisiYukle}>
              {gecmisYukleniyor ? t("mgmt.auto.loading") : t("mgmt.notif.history")}
            </button>
          )}

          {gecmisAcik && (
            <div
              className="darkCard"
              style={{
                marginTop: "10px",
                padding: "14px",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <strong style={{ color: "var(--text-primary, #e2e8f0)" }}>
                  {t("mgmt.notif.last100")}
                </strong>

                <button
                  className="ayarKapatBtn"
                  onClick={() => setGecmisAcik(false)}
                >
                  ×
                </button>
              </div>

              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <button
                  type="button"
                  className={`darkSecondaryBtn ${gecmisSekme === "duyurular" ? "active" : ""}`}
                  style={gecmisSekme === "duyurular" ? { borderColor: "rgba(99,102,241,0.7)", background: "rgba(99,102,241,0.16)" } : undefined}
                  onClick={() => setGecmisSekme("duyurular")}
                >
                  {t("mgmt.notif.tabAnnouncements")}
                </button>
                <button
                  type="button"
                  className={`darkSecondaryBtn ${gecmisSekme === "otomasyon" ? "active" : ""}`}
                  style={gecmisSekme === "otomasyon" ? { borderColor: "rgba(99,102,241,0.7)", background: "rgba(99,102,241,0.16)" } : undefined}
                  onClick={() => setGecmisSekme("otomasyon")}
                >
                  {t("mgmt.notif.tabAutomation")}
                </button>
              </div>

              {(() => {
                const gosterilecekListe = bildirimGecmisi.filter((item) =>
                  gecmisSekme === "otomasyon" ? item.tip === "automation" : item.tip !== "automation"
                );
                if (gosterilecekListe.length === 0) {
                  return (
                    <div className="emptyState">
                      {gecmisSekme === "otomasyon" ? t("mgmt.notif.noAutomationHistory") : t("mgmt.noHistory")}
                    </div>
                  );
                }
                return (
                  <div
                    className="adminScrollHistory5"
                    style={{
                      display: "grid",
                      gap: "8px",
                      paddingRight: "4px",
                    }}
                  >
                    {gosterilecekListe.map((item) => (
                      <div
                        key={item.id}
                        className="darkCard"
                        style={{
                          padding: "12px",
                          marginBottom: 0,
                        }}
                      >
                      <div
                        style={{
                          fontWeight: 800,
                          color: "#e2e8f0",
                          marginBottom: "4px",
                        }}
                      >
                        {item.baslik || t("mgmt.notif.untitled")}
                      </div>

                      <div
                        style={{
                          fontSize: "12px",
                          color: "#94a3b8",
                          lineHeight: 1.5,
                        }}
                      >
                        {item.aciklama || t("mgmt.noMessage")}
                      </div>

                      <div
                        style={{
                          marginTop: "8px",
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        {t("mgmt.notif.historyStatus")} {item.durum || "-"} · {t("mgmt.notif.historySent")}{" "}
                        <button
                          type="button"
                          onClick={() => bildirimAlicilariniToggle(item)}
                          disabled={Number(item.sentCount || 0) <= 0}
                          style={{ border:0, padding:0, background:"transparent", color: (item.sentCount || 0) > 0 ? "#22c55e" : "#64748b", fontSize:"inherit", fontWeight: (item.sentCount || 0) > 0 ? 700 : 400 }}
                        >
                          {item.sentCount || 0}
                        </button>
                        {" · "}{t("mgmt.notif.errorLabel")}:{" "}
                        <span style={{ color: (item.failedCount || 0) > 0 ? "#ef4444" : "#64748b", fontWeight: (item.failedCount || 0) > 0 ? 700 : 400 }}>
                          {item.failedCount || 0}
                        </span>
                      </div>

                      {acikBildirimAliciId === item.id && (
                        <div className="notificationRecipientMiniList">
                          {bildirimAlicilariYukleniyor === item.id ? (
                            <span>{tr ? "Alıcılar yükleniyor…" : "Loading recipients…"}</span>
                          ) : (bildirimAlicilari[item.id]?.items || []).length ? (
                            (bildirimAlicilari[item.id]?.items || []).map((recipient) => (
                              <div key={recipient.uid || recipient.email}>
                                <strong>{recipient.displayName}</strong>
                                <time>{otomasyonTarihFormatla(recipient.sentAt)}</time>
                              </div>
                            ))
                          ) : <span>{tr ? "Alıcı kaydı bulunamadı." : "No recipient record found."}</span>}
                          {(bildirimAlicilari[item.id]?.items?.length || 0) < (bildirimAlicilari[item.id]?.total || 0) && (
                            <button type="button" className="darkSecondaryBtn" onClick={() => dahaFazlaBildirimAlicisiYukle(item)}>
                              {tr ? "Daha fazla göster" : "Show more"}
                            </button>
                          )}
                        </div>
                      )}

                      <div
                        style={{
                          marginTop: "4px",
                          fontSize: "11px",
                          color: "#64748b",
                        }}
                      >
                        {t("mgmt.notif.plannedLabel")}: {item.planlananTarih || "-"} · {t("mgmt.notif.repeatLabelShort")}:{" "}
                        {item.tekrarTipi || "none"}
                      </div>
                    </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  </SuperAdminAccordion>
)}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="🕘"
          title={lang !== "en" ? "Bildirim Geçmişi" : "Notification History"}
          description={lang !== "en" ? "Tüm sunucu bildirimleri, sonuçları ve alıcı ayrıntıları" : "All server notifications, results and recipient details"}
          open={bildirimGecmisiPaneliAcik}
          onToggle={() => setBildirimGecmisiPaneliAcik((value) => !value)}
        >
          <NotificationHistoryAdmin tr={lang !== "en"} />
        </SuperAdminAccordion>
      )}
      {/* Canlı veriler yalnız panel açıldığında yüklenir; otomatik polling yapılmaz. */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="◉"
          title={`${lang !== "en" ? "Canlı Kullanıcı Aktivitesi" : "Live User Activity"}${canliAktifKullaniciSayisi == null ? "" : ` · ${canliAktifKullaniciSayisi}`}`}
          description={lang !== "en" ? "Anlık kullanıcılar, zaman aralıkları ve operasyonel dağılımlar" : "Live users, time windows and operational breakdowns"}
          open={canliAktivitePaneliAcik}
          onToggle={() => setCanliAktivitePaneliAcik((value) => !value)}
        >
          <LiveUserActivityAdmin tr={lang !== "en"} onLiveCountChange={setCanliAktifKullaniciSayisi} />
        </SuperAdminAccordion>
      )}

      {superAdminMi && (
        <SuperAdminAccordion
          icon="↗"
          title={lang !== "en" ? "Web Sitesi Trafiği" : "Website Traffic"}
          description={lang !== "en" ? "Anonim ziyaretler, kampanyalar ve mağaza yönlendirmeleri" : "Anonymous visits, campaigns and store redirects"}
          open={siteTrafficPaneliAcik}
          onToggle={() => setSiteTrafficPaneliAcik((value) => !value)}
        >
          <CorporateTrafficAdmin tr={lang !== "en"} requireAdminUnlock={false} />
        </SuperAdminAccordion>
      )}

      {superAdminMi && (
        <SuperAdminAccordion
          icon="◫"
          title={lang !== "en" ? "Hesap Silme Anketi" : "Account Deletion Survey"}
          description={lang !== "en" ? "Aile kurulumunda hesabını silen kullanıcıların geri bildirimleri" : "Feedback from users who delete their account during family setup"}
          open={hesapSilmeAnketiPaneliAcik}
          onToggle={() => setHesapSilmeAnketiPaneliAcik((value) => !value)}
        >
          <AccountDeletionSurveyAdminPanel tr={lang !== "en"} managementContext />
        </SuperAdminAccordion>
      )}

      {/* ─────────────────────────────────────────
          🎯 KULLANICI HATIRLATMA MERKEZİ (Sadece SuperAdmin)
      ───────────────────────────────────────── */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="🎯"
          title={lang !== "en" ? "Kullanıcı Hatırlatma Merkezi" : "User Reminder Center"}
          description={lang !== "en" ? "Kayıt, kullanım ve pasif kullanıcı hatırlatmalarını yönetin" : "Manage onboarding, usage and inactive-user reminders"}
          open={hatirlatmaMerkeziAcik}
          onToggle={() => setHatirlatmaMerkeziAcik((value) => !value)}
        >
          <EngagementAdminPanel tr={lang !== "en"} compact={false} />
        </SuperAdminAccordion>
      )}

      {/* ─────────────────────────────────────────
          ⭐ MAĞAZA DEĞERLENDİRMELERİ (Sadece SuperAdmin)
      ───────────────────────────────────────── */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="⭐"
          title={lang !== "en" ? "Mağaza Değerlendirmeleri" : "Store Review Management"}
          description={lang !== "en" ? "App Store ve Google Play değerlendirme akışlarını yönetin" : "Manage App Store and Google Play review flows"}
          open={magazaDegerlendirmePaneliAcik}
          onToggle={() => setMagazaDegerlendirmePaneliAcik((value) => !value)}
        >
          {magazaDegerlendirmePaneliAcik && <AppReviewAdminPanel tr={lang !== "en"} />}
        </SuperAdminAccordion>
      )}

      {/* ─────────────────────────────────────────
          📧 MAIL SİSTEMİ TESTİ (Sadece SuperAdmin)
      ───────────────────────────────────────── */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="📧"
          title={t("mgmt.mail.title")}
          description={t("mgmt.mail.desc")}
          open={mailTestPaneliAcik}
          onToggle={() => setMailTestPaneliAcik((value) => !value)}
        >
          {mailTestPaneliAcik && (
            <EmailSystemAdmin t={t} lang={lang} defaultEmail={profil?.email || ""} />
          )}
        </SuperAdminAccordion>
      )}

      {/* ─────────────────────────────────────────
          🤖 AI ASİSTAN BİLDİRİMLERİ (Sadece SuperAdmin)
      ───────────────────────────────────────── */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="🤖"
          title={lang !== "en" ? "AI Asistan Bildirim Ayarları" : "AI Assistant Notification Settings"}
          description={lang !== "en" ? "Kişiselleştirilmiş AI bildirimlerini ve gönderim kurallarını yönetin" : "Manage personalized AI notifications and delivery rules"}
          open={aiBildirimPaneliAcik}
          onToggle={() => setAiBildirimPaneliAcik((value) => !value)}
        >
          <AssistantNotificationAdmin />
        </SuperAdminAccordion>
      )}

      {/* ─────────────────────────────────────────
          💎 PREMIUM AYARLARI (Sadece SuperAdmin)
      ───────────────────────────────────────── */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="💎"
          title={t("mgmt.premiumSettings")}
          description={t("mgmt.premiumDesc")}
          open={premiumPanelAcik}
          onToggle={() => setPremiumPanelAcik((value) => !value)}
        >
          <div className="premiumAdminCard">
          {/* Global aktif/pasif — her zaman görünür */}
          <div className={`premiumAdminStatus ${localPremiumConfig.premiumEnabled ? "enabled" : "disabled"}`}>
            <div className="premiumAdminStatusText">
              <div className="premiumAdminStatusTitle">
                {localPremiumConfig.premiumEnabled ? t("mgmt.premium.on") : t("mgmt.premium.off")}
              </div>
              <div className="premiumAdminStatusDesc">
                {localPremiumConfig.premiumEnabled
                  ? t("mgmt.premium.activeDesc")
                  : t("mgmt.premium.inactiveDesc")}
              </div>
            </div>
            <button
              className={`premiumAdminPillBtn ${localPremiumConfig.premiumEnabled ? "active" : "passive"}`}
              onClick={() => setPremiumEnabled(!localPremiumConfig.premiumEnabled)}
            >
              {localPremiumConfig.premiumEnabled ? "Aktif" : "Pasif"}
            </button>
          </div>

          {/* Hızlı kaydet butonu — her zaman görünür */}
          {premiumKayitMesaj && (
            <div className={`premiumAdminMessage ${premiumKayitMesaj.includes("✓") ? "success" : "error"}`}>
              {premiumKayitMesaj}
            </div>
          )}

            <div className="premiumAdminPanel">

              {/* ── LİMİTLER ── */}
              <div className="premiumAdminSectionTitle">📊 {t("mgmt.freeLimits")}</div>
              <p className="premiumAdminSectionDesc">
                {t("mgmt.freeLimitsDesc")}
              </p>

              <div className="premiumAdminList">
                {[
                  { key: "alisverisOge",     label: t("mgmt.limit.shopping"), unit: t("mgmt.limit.unit.item") },
                  { key: "aktifGorev",       label: t("mgmt.limit.task"), unit: t("mgmt.limit.unit.task") },
                  { key: "aylikTakvim",      label: t("mgmt.limit.calendar"), unit: t("mgmt.limit.unit.perMonth") },
                  { key: "aylikFinans",      label: t("mgmt.limit.finance"), unit: t("mgmt.limit.unit.perMonth") },
                  { key: "saglikKayitKisi",  label: t("mgmt.limit.health"), unit: t("mgmt.limit.unit.perPerson") },
                  { key: "cocukProfil",      label: t("mgmt.limit.children"), unit: t("mgmt.limit.unit.profile") },
                  { key: "belgeSayisi",      label: t("mgmt.limit.documents"), unit: t("mgmt.limit.unit.doc") },
                  { key: "belgeDepoMB",      label: "Belge deposu",           unit: "MB" },
                  { key: "mesajGecmis",      label: t("mgmt.limit.messages"), unit: t("mgmt.limit.unit.message") },
                  { key: "kayitliYer",       label: t("mgmt.limit.mapPlaces"), unit: t("mgmt.limit.unit.place") },
                  { key: "aileUye",          label: t("mgmt.limit.members"), unit: t("mgmt.limit.unit.member") },
                  { key: "kullaniciAile",    label: t("mgmt.limit.userFamilies"), unit: t("mgmt.limit.unit.family") },
                  { key: "premiumPlusToplamUye", label: t("mgmt.limit.plusMembers"), unit: t("mgmt.limit.unit.member") },
                  { key: "gunlukNePisirsam", label: t("mgmt.limit.mealSuggestion"), unit: t("mgmt.limit.unit.perDay") },
                ].map(({ key, label, unit }) => (
                  <div key={key} className="premiumAdminLimitRow">
                    <div className="premiumAdminRowText">
                      <div className="premiumAdminRowTitle">{label}</div>
                      <div className="premiumAdminRowMeta">{t("mgmt.default")}: {DEFAULT_PREMIUM_CONFIG.limits[key]} {unit}</div>
                    </div>
                    <input
                      type="number" min="1" max="9999"
                      className="darkInput premiumAdminNumberInput"
                      value={localPremiumConfig.limits[key] ?? DEFAULT_PREMIUM_CONFIG.limits[key]}
                      onChange={(e) => setLimit(key, e.target.value)}
                    />
                  </div>
                ))}
              </div>

              {/* ── KİLİTLİ ÖZELLİKLER ── */}
              <div className="premiumAdminSectionTitle premiumAdminSectionGap">🔒 {t("mgmt.lockedFeatures")}</div>
              <p className="premiumAdminSectionDesc">
                {t("mgmt.lockedDesc")}
              </p>

              <div className="premiumAdminList">
                {[
                  { key: "finansRapor",         label: t("management.feature.financeReport") },
                  { key: "finansBirikim",       label: t("management.feature.financeSavings") },
                  { key: "finansTaksit",        label: t("management.feature.financeInstallments") },
                  { key: "finansAbonelik",      label: t("management.feature.financeSubscriptions") },
                  { key: "finansFatura",        label: t("management.feature.financeBills") },
                  { key: "canliKonum",          label: t("mgmt.limit.liveLocation") },
                  { key: "geofence",            label: t("mgmt.limit.geofence") },
                  { key: "konumGecmisi",        label: t("mgmt.limit.locationHistory") },
                  { key: "evcilHayvanProfil",   label: t("mgmt.limit.petProfile") },
                  { key: "haftalikMenu",        label: t("mgmt.limit.weeklyMenu") },
                  { key: "cokluSohbetOdasi",    label: t("mgmt.limit.multiRoom") },
                  { key: "zamanlanmisBildirim", label: t("mgmt.limit.scheduledNotif") },
                  { key: "otomatikBildirim",    label: t("management.feature.autoNotifications") },
                  { key: "cokluAile",           label: t("mgmt.limit.multiFamily") },
                ].map(({ key, label }) => {
                  const isLocked = localPremiumConfig.lockedFeatures[key] !== false
                    ? (localPremiumConfig.lockedFeatures[key] ?? DEFAULT_PREMIUM_CONFIG.lockedFeatures[key])
                    : false;
                  return (
                    <div key={key} className={`premiumAdminFeatureRow ${isLocked ? "locked" : "open"}`}>
                      <div className="premiumAdminRowText">
                        <div className="premiumAdminRowTitle">{label}</div>
                        <div className="premiumAdminFeatureState">
                          {isLocked ? t("mgmt.premium.locked") : t("mgmt.premium.open2")}
                        </div>
                      </div>
                      <button
                        className={`premiumAdminPillBtn ${isLocked ? "active" : "passive"}`}
                        onClick={() => setLocked(key, !isLocked)}
                      >
                        {isLocked ? t("mgmt.premium.lockedLabel") : t("mgmt.premium.openLabel")}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* ── KAYDET ── */}
              <div className="premiumAdminActions">
                <button className="primaryButton" onClick={premiumKaydet} disabled={premiumKaydediliyor}>
                  {premiumKaydediliyor ? t("mgmt.premium.saving") : t("mgmt.premium.saveBtn")}
                </button>
                <button className="darkSecondaryBtn" onClick={premiumVarsayilanaYukle}>
                  ↩ {t("mgmt.resetPremiumDefaults")}
                </button>
              </div>

            </div>
          </div>
        </SuperAdminAccordion>
      )}


      {/* Profesyonel salt-okunur operasyon özetleri — sayfanın en altında. */}
      {superAdminMi && (
        <SuperAdminAccordion
          icon="🧭"
          title={lang !== "en" ? "Operasyon ve Sistem Merkezi" : "Operations and System Center"}
          description={lang !== "en" ? "Sistem, kullanıcı, bildirim, güvenlik ve depolama görünümü" : "System, user, notification, security and storage overview"}
          open={operasyonMerkeziAcik}
          onToggle={() => setOperasyonMerkeziAcik((value) => !value)}
        >
          <PerformanceMonitoringAdmin tr={lang !== "en"} requireAdminUnlock={false} />
          <AdminOperationsOverview tr={lang !== "en"} />
        </SuperAdminAccordion>
      )}


      {seciliYetkiUye && (
        <PermissionModal
          uye={seciliYetkiUye}
          profil={profil}
          kapat={() => setSeciliYetkiUye(null)}
          yetkiKaydet={yetkiKaydet}
          uyeSifreSifirla={uyeSifreSifirla}
          uyeYetkiDegistir={uyeYetkiDegistir}
          uyeRolDegistir={uyeRolDegistir}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   YETKİ MODAL
───────────────────────────────────────── */
function PermissionModal({ uye, profil, kapat, yetkiKaydet, uyeSifreSifirla, uyeYetkiDegistir, uyeRolDegistir }) {
  const { t, lang } = useLanguage();

  // Dil bazlı dinamik listeler
  const pageNavMap = {
    home: t("nav.home"), shopping: t("nav.shopping"), calendar: t("nav.calendar"),
    finance: t("nav.finance"), tasks: t("nav.tasks"), sharedExpenses: t("nav.sharedExpenses"),
    health: t("nav.health"), harita: t("nav.map"), children: t("nav.children"),
    chat: t("nav.chat"), documents: t("nav.documents"), nePisirsam: t("nav.cooking"),
    activity: t("nav.activity"), assistant: t("nav.assistant"), userManagement: t("nav.management"),
  };
  const SAYFA_YETKILERI = SAYFA_YETKILERI_KEYS.map(s => ({ ...s, label: pageNavMap[s.key] || s.key }));
  const ISLEM_GRUPLARI = ISLEM_GRUPLARI_KEYS.map(g => ({
    key: g.baslik,
    baslik: t(g.baslik),
    asset: g.asset,
    items: g.items.map(i => ({ key: i.key, label: t(i.labelKey) })),
  }));

  const kendiUyeKaydi = Boolean(
    (uye?.uid && profil?.uid && uye.uid === profil.uid) ||
    (uye?.id && profil?.uid && uye.id === profil.uid) ||
    (uye?.email && profil?.email && uye.email === profil.email)
  );
  const [uyelikTipi, setUyelikTipi] = useState(uye?.aileYetkisi || uye?.role || "Aile Üyesi");
  const hedefYoneticiMi = familyAdminRole(uyelikTipi);

  const kilitliSayfaMi = (key) => kendiUyeKaydi || (hedefYoneticiMi && SELF_ADMIN_LOCKED_PAGE_KEYS.includes(key));
  const kilitliIslemMi = (key) => kendiUyeKaydi || (hedefYoneticiMi && SELF_ADMIN_LOCKED_ACTION_KEYS.includes(key));
  const guvenliYetki = (permissions) => {
    if (!hedefYoneticiMi) return permissions;
    return normalizeSelfAdminPermissions(permissions);
  };

  /* Mevcut izinleri base ile güvenli birleştir */
  const [perms, setPerms] = useState(() => {
    const base = permissionTemplates.standartUye.permissions;
    const existing = uye.permissions || {};
    return guvenliYetki({
      pages: { ...base.pages, ...(existing.pages || {}) },
      actions: { ...base.actions, ...(existing.actions || {}) },
    });
  });

  const [sablon, setSablon] = useState("ozel");
  const [acikGrup, setAcikGrup] = useState(null);
  const [sekme, setSekme] = useState("sayfalar");
  const [sifreGonderildi, setSifreGonderildi] = useState(false);
  const [kayitYukleniyor, setKayitYukleniyor] = useState(false);
  const [kayitMesaj, setKayitMesaj] = useState("");
  const [profilRol, setProfilRol] = useState(uye?.rol || uye?.profilRol || "");
  const [sifreHata, setSifreHata] = useState("");
  const [sekmeAnimasyon, setSekmeAnimasyon] = useState("");
  const sekmeTouchRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (hedefYoneticiMi) setPerms((current) => normalizeSelfAdminPermissions(current));
  }, [hedefYoneticiMi]);

  const permissionTabs = [
    { key: "sayfalar", label: t("management.tab.pages"), icon: "pages" },
    { key: "islemler", label: t("management.tab.actions"), icon: "actions" },
    { key: "profil", label: t("management.tab.profile"), icon: "profile" },
    { key: "sifre", label: t("management.tab.password"), icon: "password" },
  ];

  const sekmeyeGec = (nextKey, direction = "") => {
    if (!nextKey || nextKey === sekme) return;
    setSekmeAnimasyon(direction ? `slide-${direction}` : "fade");
    setSekme(nextKey);
  };

  const tabTouchProps = {
    onTouchStart: (event) => {
      const touch = event.touches?.[0];
      if (touch) sekmeTouchRef.current = { x: touch.clientX, y: touch.clientY };
    },
    onTouchEnd: (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      const dx = touch.clientX - sekmeTouchRef.current.x;
      const dy = Math.abs(touch.clientY - sekmeTouchRef.current.y);
      if (Math.abs(dx) < 55 || dy > 70) return;
      const index = permissionTabs.findIndex((item) => item.key === sekme);
      const nextIndex = dx < 0 ? index + 1 : index - 1;
      if (nextIndex < 0 || nextIndex >= permissionTabs.length) return;
      sekmeyeGec(permissionTabs[nextIndex].key, dx < 0 ? "left" : "right");
    },
    onAnimationEnd: () => setSekmeAnimasyon(""),
  };

  const sablonUygula = (key) => {
    if (kendiUyeKaydi) return;
    setSablon(key);
    if (key !== "ozel") {
      const tpl = permissionTemplates[key].permissions;
      const templatePermissions = {
        pages:   { ...permissionTemplates.standartUye.permissions.pages,   ...tpl.pages },
        actions: { ...permissionTemplates.standartUye.permissions.actions, ...tpl.actions },
      };
      setPerms(hedefYoneticiMi ? normalizeSelfAdminPermissions(templatePermissions) : templatePermissions);
    }
  };

  const togglePage = (key) => {
    if (kilitliSayfaMi(key)) return;
    setPerms((p) => ({ ...p, pages: { ...p.pages, [key]: !p.pages[key] } }));
  };
  const toggleAction = (key) => {
    if (kilitliIslemMi(key)) return;
    setPerms((p) => ({ ...p, actions: { ...p.actions, [key]: !p.actions[key] } }));
  };

  /* Tüm grubu aç/kapat */
  const toggleGrup = (grup) => {
    if (kendiUyeKaydi) return;
    const hepsiAcik = grup.items.every((i) => perms.actions[i.key]);
    const yeni = {};
    grup.items.forEach((i) => { yeni[i.key] = kilitliIslemMi(i.key) ? true : !hepsiAcik; });
    setPerms((p) => guvenliYetki({ ...p, actions: { ...p.actions, ...yeni } }));
  };

  const sayfaAktif  = SAYFA_YETKILERI.filter((s) => perms.pages[s.key]).length;
  const islemAktif  = Object.values(perms.actions).filter(Boolean).length;

  const kaydet = async () => {
    setKayitYukleniyor(true);
    try {
      const uyeId = uye.uid || uye.id;
      const kaydedilecekYetki = familyAdminRole(uyelikTipi)
        ? normalizeSelfAdminPermissions(perms)
        : perms;
      if (profilRol && profilRol !== (uye?.rol || uye?.profilRol || "")) {
        await uyeRolDegistir?.(uyeId, profilRol);
      }
      if (!kendiUyeKaydi) {
        if (uyelikTipi && uyelikTipi !== (uye?.aileYetkisi || uye?.role || "Aile Üyesi")) {
          await uyeYetkiDegistir?.(uyeId, uyelikTipi);
        }
        await yetkiKaydet(uye.id, kaydedilecekYetki);
      }
      setKayitMesaj(t("management.saved"));
      setTimeout(() => { setKayitMesaj(""); kapat(); }, 1200);
    } catch {
      setKayitMesaj(t("management.saveFailed"));
    } finally {
      setKayitYukleniyor(false);
    }
  };

  return (
    <div className="modalOverlay" onClick={kapat}>
      <div className="permModalDark" onClick={(e) => e.stopPropagation()}>

        {/* Başlık */}
        <div className="permModalHeader">
          <div className="memberAvatar">
            <AvatarImg
              avatar={uye.avatar || null}
              renk={uye.calendarColor || "#6366f1"}
              initials={`${uye.ad?.[0]||""}${uye.soyad?.[0]||""}`}
              size={38}
              glow={false}
              radius="50%"
            
                      uid={uye.uid || uye.id}
                      isOnline={uye.isOnline === true || uye.online === true}
                    />
          </div>
          <div>
            <div className="permModalName">{uye.ad} {uye.soyad}</div>
            <div className="permModalRole">{memberRoleLine(lang, uye, t)}</div>
          </div>
          <button className="ayarKapatBtn" style={{ marginLeft: "auto" }} onClick={kapat}>×</button>
        </div>

        {/* Özet */}
        <div className="permStatRow">
          <div className="permStatDark"><span>{sayfaAktif}</span><small>{t("management.pageCount")}</small></div>
          <div className="permStatDark"><span>{islemAktif}</span><small>{t("management.actionCount")}</small></div>
        </div>

        {/* Şablon */}
        <div className="inputLabelGroup" style={{ marginBottom: "12px" }}>
          <label className="darkLabel">{t("management.template")}</label>
          <div className="permTemplateRail" role="list">
            {[
              ["ozel", "custom", t("management.template.custom")],
              ["tamYetkili", "fullAccess", t("management.template.full")],
              ["standartUye", "standard", t("management.template.standard")],
              ["cocukProfili", "childProfile", t("management.template.child")],
              ["sadeceGoruntule", "viewOnly", t("management.template.viewonly")],
            ].map(([key, icon, label]) => (
              <button
                type="button"
                role="listitem"
                key={key}
                className={`permTemplateCard ${sablon === key ? "active" : ""}`}
                onClick={() => sablonUygula(key)}
                disabled={kendiUyeKaydi}
              >
                <AppFeatureIcon type={icon} />
                <span>{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sekmeler */}
        <div className="permModalTabs">
          {permissionTabs.map((item) => (
            <button
              key={item.key}
              className={sekme === item.key ? "permModalTab active" : "permModalTab"}
              onClick={() => sekmeyeGec(item.key)}
            >
              <AppFeatureIcon type={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>

        {/* ── SAYFALAR ── */}
        {sekme === "sayfalar" && (
          <div className={`permPageGrid permTabPanel ${sekmeAnimasyon}`} {...tabTouchProps}>
            {SAYFA_YETKILERI.map((s) => {
              const kilitli = kilitliSayfaMi(s.key);
              return (
              <button
                key={s.key}
                className={`permPageBtn ${perms.pages[s.key] ? "on" : ""}`}
                onClick={() => togglePage(s.key)}
                disabled={kilitli}
                title={kilitli ? t("mgmt.notif.selfAdmin") : undefined}
                style={kilitli ? { cursor: "not-allowed", opacity: 0.78 } : undefined}
              >
                <span className="permAssetIcon"><img src={s.asset} alt="" aria-hidden="true" /></span>
                <span className="permPageLabel">{s.label}</span>
              </button>
            );
            })}
          </div>
        )}

        {/* ── İŞLEMLER ── */}
        {sekme === "islemler" && (
          <div className={`permTabPanel ${sekmeAnimasyon}`} {...tabTouchProps}>
            {ISLEM_GRUPLARI.map((grup) => {
              const acik = acikGrup === grup.baslik;
              const aktifSayisi = grup.items.filter((i) => perms.actions[i.key]).length;
              const hepsiAcik   = aktifSayisi === grup.items.length;
              return (
                <div key={grup.key} className="permGroupDark">
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <button
                      className="permGroupHeaderDark"
                      style={{ flex: 1 }}
                      onClick={() => setAcikGrup(acik ? null : grup.baslik)}
                    >
                      <span className="permGroupIdentity">
                        <span className="permAssetIcon"><img src={grup.asset} alt="" aria-hidden="true" /></span>
                        <span>{grup.baslik}</span>
                      </span>
                      <span className="permGroupCount">{aktifSayisi}/{grup.items.length}</span>
                      <span className="permGroupChevron">{acik ? "▴" : "▾"}</span>
                    </button>
                    {/* Tümünü aç/kapat butonu */}
                    <button
                      onClick={() => toggleGrup(grup)}
                      disabled={kendiUyeKaydi}
                      title={hedefYoneticiMi && grup.items.some((i) => kilitliIslemMi(i.key)) ? t("mgmt.notif.selfAdmin") : undefined}
                      style={{
                        background: hepsiAcik ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: "8px",
                        color: hepsiAcik ? "#a78bfa" : "#64748b",
                        fontSize: "10px",
                        fontWeight: 800,
                        padding: "4px 8px",
                        cursor: hedefYoneticiMi && grup.items.some((i) => kilitliIslemMi(i.key)) ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {hepsiAcik ? t("common.cancel") : t("common.all")}
                    </button>
                  </div>

                  {acik && (
                    <div className="permGroupItemsDark">
                      {grup.items.map((item) => {
                        const kilitli = kilitliIslemMi(item.key);
                        return (
                        <label
                          key={item.key}
                          className="permSwitchRowDark"
                          title={kilitli ? t("management.locked") || "" : undefined}
                          style={kilitli ? { cursor: "not-allowed", opacity: 0.78 } : undefined}
                        >
                          <span className="permSwitchLabelDark">{item.label}</span>
                          <div
                            className={`toggleSwitch sm ${perms.actions[item.key] ? "on" : ""}`}
                            onClick={() => toggleAction(item.key)}
                            style={kilitli ? { cursor: "not-allowed" } : undefined}
                          >
                            <div className="toggleThumb" />
                          </div>
                        </label>
                      );
                    })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}


        {/* ── PROFİL / ÜYELİK ── */}
        {sekme === "profil" && (
          <div className={`permProfilePremiumBox permTabPanel ${sekmeAnimasyon}`} {...tabTouchProps}>
            <div className="permProfilePremiumHeader">
              <span>✨ Premium</span>
              <strong>{t("management.profileRoleTitle")}</strong>
              <small>{t("management.profileRoleDesc")}</small>
            </div>

            <div className="inputLabelGroup">
              <label className="darkLabel">{t("management.memberRole")}</label>
              <select className="darkInput" value={profilRol} onChange={(e) => setProfilRol(e.target.value)}>
                <option value="">{t("management.selectRole")}</option>
                {FAMILY_PROFILE_ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>{mLabel(lang, role, role)}</option>
                ))}
              </select>
            </div>

            <div className="inputLabelGroup">
              <label className="darkLabel">{t("management.membershipType")}</label>
              <select className="darkInput" value={uyelikTipi} onChange={(e) => setUyelikTipi(e.target.value)} disabled={kendiUyeKaydi}>
                {FAMILY_MEMBERSHIP_OPTIONS.map((type) => (
                  <option key={type} value={type}>{mLabel(lang, type, type)}</option>
                ))}
              </select>
            </div>

            <p className="ayarAciklama" style={{ marginTop: 10 }}>
              {t("management.profileRoleSyncDesc")}
            </p>
          </div>
        )}

        {/* ── ŞİFRE ── */}
        {sekme === "sifre" && (
          <div className={`permSifreBolum permTabPanel ${sekmeAnimasyon}`} {...tabTouchProps}>
            <p className="darkLabel" style={{ color: "#94a3b8", lineHeight: 1.5 }}>
              {t("management.passwordResetDesc")}
            </p>
            <div className="permSifreEmail">
              <AppFeatureIcon type="mail" />
              <span style={{ color: "#e2e8f0", fontWeight: 700 }}>
                {uye.email || t("management.noEmail") || t("management.emailNotFound")}
              </span>
            </div>
            {sifreGonderildi ? (
              <div className="profilSuccess">{t("management.passwordResetSent")}</div>
            ) : (
              <button
                className="primaryButton"
                onClick={async () => {
                  setSifreHata("");
                  try {
                    await uyeSifreSifirla(uye.email);
                    setSifreGonderildi(true);
                  } catch (err) {
                    setSifreHata(err?.message || t("management.passwordResetFailed"));
                  }
                }}
              >
                {t("management.passwordReset")}
              </button>
            )}
            {sifreHata && <div className="profilError" style={{ marginTop: 10 }}>{sifreHata}</div>}
          </div>
        )}

        {/* Alt butonlar */}
        <div className="permModalFooter">
          {kayitMesaj && (
            <div className={kayitMesaj.includes("✓") ? "profilSuccess" : "profilError"} style={{ marginBottom: "8px" }}>
              {kayitMesaj}
            </div>
          )}
          <button className="primaryButton" onClick={kaydet} disabled={kayitYukleniyor}>
            {kayitYukleniyor ? t("management.saving") : `✓ ${t("management.save")}`}
          </button>
          <button className="darkSecondaryBtn" style={{ marginTop: "8px" }} onClick={kapat}>
            {mLabel(lang, "Kapat")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UserManagementPage;
